import { __, setLocale } from '../translator';
import { getGeoData } from '../geo-location';
import { collectOnshoreFieldAnswers } from './onshore-field-answers';
import * as Sentry from "@sentry/browser";
import { awaitLandingAutosaveForWizardAdvance, updateStatusAfterSubmit } from './auto-save';
import { getLatestCalendarFinalData, buildCalendarFinalDetailsHtml } from './calendar-final-details';
import {
    applyCountrySelect2Templates,
    formatCountrySelect2Option,
    stripLeadingFlagChars,
} from './landing-country-flags';
import {
    ensureWizardLabelInvalidHelper as setupWizardLabelInvalidHelper,
    focusFirstFieldInWizardStep,
    wizardSyncPhoneValidationState as syncWizardPhoneValidationState,
} from './landing-wizard-validation';

class LandingQuestionsLoader {
    constructor(options = {}) {
        Object.assign(this, options);
        this.nonExtraQuestionIds = [];
        this.countryName = null;
        this.countryCode = null;
        this.hasMultipleDestinationsForCity = false;
        this.embedHowContinueDeferred = false;
        this.wizardEmbedExtraStepCount = 0;
        this.wizardSubmitSettled = false;
        this._wizardSubmitInFlight = false;
        this._extraVisibilityRafId = null;
        this.wizardPrevBusy = false;
        this.questionsRulesEnabled = false;
        this.questionsRefreshEnabled = false;
        this._studentTypeQuestionIds = new Set();
        this._questionRulesRequestSeq = 0;
        this._questionRulesWatcherBound = false;
        this._preservedFieldValues = {};
        this._suppressQuestionRulesRefresh = false;
        this._preservingFieldValues = false;
        this._resumeWizardFieldId = null;
        this._lastVisibleQuestionKey = null;
        this._wizardVisitStack = [];
        this._rulesRefreshPromise = null;

        this.initSentry();
        
        if (!this.landingId || !this.lang) {
            const errorMessage = 'LandingQuestionsLoader: landingId and lang are required';
            console.error(errorMessage);
            this.logToSentry(new Error(errorMessage), { context: 'constructor' });
            return;
        }

        this.questionsApiLocale =
            this.wizardUiLocale !== undefined && this.wizardUiLocale !== null && String(this.wizardUiLocale).trim() !== ''
                ? String(this.wizardUiLocale).trim()
                : (this.embedFormLayout === 'wizard' ? 'es' : this.lang);

        setLocale(this.questionsApiLocale);
        window.__landingQuestionsLoaderInstance = this;
        if (LandingQuestionsLoader._calendarSubmitLifecycleBound !== true) {
            LandingQuestionsLoader._calendarSubmitLifecycleBound = true;
            $(document).on('questionsLoaded.landingQuestionsLoader', () => {
                const loader = window.__landingQuestionsLoaderInstance;
                if (!loader) {
                    return;
                }
                if (loader.isNonCriteriaFlow()) {
                    $('#calendar-wrapper').addClass('hidden');
                }
                $('#btn-register').prop('disabled', false);
                loader.syncCalendarRegisterSubmitBinding();
            });
            $(document).on('hasCalendarChanged.landingQuestionsLoader', () => {
                window.__landingQuestionsLoaderInstance?.syncCalendarRegisterSubmitBinding?.();
            });
        }
        this.init();
        this.ensureWizardLabelInvalidHelper();
        this.initFormValidation();
        this.initLanguageSwitcher();
    }

    initSentry() {
        if (typeof Sentry !== 'undefined' && window.sentryDsn) {
            Sentry.init({
                dsn: window.sentryDsn,
                integrations: [
                    Sentry.browserTracingIntegration(),
                ],
            });
        }
    }

    logToSentry(error, extraData = {}) {
        if (typeof Sentry === 'undefined') return;

        Sentry.withScope(scope => {
            if (extraData.context) scope.setTag("context", extraData.context);
            if (extraData.extra) scope.setExtras(extraData.extra);
            
            scope.setTag("landing_id", this.landingId);
            
            Sentry.captureException(error);
        });
    }

    async init() {
        await this.getCountryName();
        this.loadQuestions();
    }

    async getCountryName() {
        const data = await getGeoData();
        this.countryCode = data?.country_code?.toLowerCase() ?? null;
        this.countryName = data?.country_name ?? null;
    }

    buildVisibleQuestionKey(questions) {
        if (!Array.isArray(questions)) {
            return '';
        }
        return questions
            .filter((field) => field && !field.is_extra)
            .map((field) => String(field.id))
            .join('|');
    }

    syncOptionButtonSelectionState($scope) {
        const $root = $scope && $scope.length ? $scope : $('#questions-content');
        $root.find('input.landing-embed-option-value').each((_, el) => {
            const $el = $(el);
            const val = $el.val();
            const hasVal = val !== null && val !== undefined && String(val).trim() !== '';
            const $group = $el.closest('.control').find('.landing-embed-option-buttons');
            $group.find('.landing-embed-option-btn').removeClass('is-selected');
            if (!hasVal) {
                return;
            }
            $group.find('.landing-embed-option-btn').filter(function () {
                return String($(this).attr('data-value')) === String(val);
            }).addClass('is-selected');
        });
    }

    loadQuestions(options = {}) {
        const preserveValues = options.preserveValues === true;
        const answers = options.answers || null;
        const focusSnapshot = preserveValues ? this.snapshotActiveFieldFocus() : null;

        this._preservingFieldValues = preserveValues;

        if (!preserveValues) {
            $("#questions-loader").removeClass('hidden');
            $("#questions-content").addClass('hidden').empty();
            this.embedHowContinueDeferred = false;
            this._resumeWizardFieldId = null;
            this._lastVisibleQuestionKey = null;
            this._wizardVisitStack = [];
            this._rulesRefreshPromise = null;
        } else {
            this._preservedFieldValues = this.snapshotFieldValues();
            this._resumeWizardFieldId = this.captureWizardStepAnchor();
        }

        const params = new URLSearchParams({
            locale: this.questionsApiLocale,
        });
        if (answers) {
            Object.entries(answers).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    params.set(key, value);
                }
            });
        }

        const seq = ++this._questionRulesRequestSeq;
        return new Promise((resolve) => {
            $.ajax({
                url: `/${this.questionsApiLocale}/landing/load-questions/${this.landingId}?${params.toString()}`,
                type: 'GET',
                success: (response) => {
                    if (seq !== this._questionRulesRequestSeq) {
                        resolve(false);
                        return;
                    }
                    try {
                        this.questionsRulesEnabled = !!response.questions_rules_enabled;
                        this.questionsRefreshEnabled = this.questionsRulesEnabled || !!response.has_student_type_question;
                        this._studentTypeQuestionIds = new Set((response.data || []).filter((q) => q.is_student_type).map((q) => String(q.id)));
                        const visibleKey = this.buildVisibleQuestionKey(response.data);
                        const nextVisibleIds = new Set(String(visibleKey || '').split('|').filter(Boolean));

                        // Same visible set: keep DOM so selection chrome stays intact.
                        if (preserveValues && visibleKey && visibleKey === this._lastVisibleQuestionKey) {
                            this._preservingFieldValues = false;
                            this._resumeWizardFieldId = null;
                            this.syncOptionButtonSelectionState();
                            resolve(true);
                            return;
                        }

                        if (preserveValues) {
                            const $phone = $('#phone-wrapper');
                            if ($phone.length && $phone.closest('#questions-content').length) {
                                $phone.insertBefore('#questions-content');
                            }
                            this._suppressQuestionRulesRefresh = true;
                            if (this._preservedFieldValues) {
                                Object.keys(this._preservedFieldValues).forEach((id) => {
                                    if (!nextVisibleIds.has(String(id))) {
                                        delete this._preservedFieldValues[id];
                                    }
                                });
                            }
                        }
                        this.renderQuestions(response.data);
                        this._lastVisibleQuestionKey = visibleKey;
                        if (preserveValues) {
                            this.restoreFieldValues(this._preservedFieldValues);
                            this._suppressQuestionRulesRefresh = true;
                            this.populateCityOptions($('#destination').val(), this._preservedFieldValues?.city ?? null, false);
                            this._suppressQuestionRulesRefresh = false;
                            this.syncOptionButtonSelectionState();
                            this.restoreActiveFieldFocus(focusSnapshot);
                            this._suppressQuestionRulesRefresh = false;
                            this.triggerChangeForNewlyPreselectedCountryFields(this._preservedFieldValues);
                        }
                        this._preservingFieldValues = false;
                        if (this.questionsRefreshEnabled) {
                            this.setupQuestionRulesWatcher();
                            if (!preserveValues) {
                                this.refreshQuestionsForPrefilledAnswers();
                            }
                        }
                        resolve(true);
                    } catch (e) {
                        this._suppressQuestionRulesRefresh = false;
                        this._preservingFieldValues = false;
                        console.error('Error rendering questions:', e);

                        this.logToSentry(e, {
                            context: 'renderQuestions logic',
                            extra: {
                                response: JSON.stringify(response)
                            }
                        });

                        this.showErrorMessage('Error processing questions.');
                        resolve(false);
                    }
                },
                error: (xhr, status, error) => {
                    this._preservingFieldValues = false;
                    if (seq !== this._questionRulesRequestSeq) {
                        resolve(false);
                        return;
                    }
                    console.error('Failed to load questions:', error);

                    this.logToSentry(new Error(`API Error: ${status} - ${error}`), {
                        context: 'ajax_load_questions',
                        extra: {
                            status: xhr.status,
                            response: xhr.responseText ? xhr.responseText : 'empty'
                        }
                    });

                    this.showErrorMessage('Error loading questions.');
                    resolve(false);
                }
            });
        });
    }

    snapshotFieldValues() {
        const values = {};
        const $scope = $('#questions-content').add('#phone-wrapper');
        $scope.find('input, select, textarea').each(function () {
            const $el = $(this);
            const id = $el.attr('id');
            if (!id || $el.is(':checkbox') || $el.is(':radio')) {
                return;
            }
            const val = $el.val();
            if (val !== null && val !== undefined && val !== '') {
                values[id] = val;
            }
        });
        return values;
    }

    snapshotActiveFieldFocus() {
        const el = document.activeElement;
        if (!el || !el.closest) {
            return null;
        }
        if (!el.closest('#questions-content') && !el.closest('#phone-wrapper')) {
            return null;
        }
        const id = el.id || null;
        if (!id) {
            return null;
        }
        const snapshot = { id };
        if (typeof el.selectionStart === 'number' && typeof el.selectionEnd === 'number') {
            snapshot.selectionStart = el.selectionStart;
            snapshot.selectionEnd = el.selectionEnd;
        }
        return snapshot;
    }

    restoreActiveFieldFocus(snapshot) {
        if (!snapshot || !snapshot.id) {
            return;
        }
        const el = document.getElementById(snapshot.id);
        if (!el || typeof el.focus !== 'function') {
            return;
        }
        el.focus();
        if (
            typeof snapshot.selectionStart === 'number'
            && typeof snapshot.selectionEnd === 'number'
            && typeof el.setSelectionRange === 'function'
        ) {
            try {
                el.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd);
            } catch (e) {
                // Some input types (e.g. email in older browsers) reject setSelectionRange.
            }
        }
    }

    restoreFieldValues(values) {
        if (!values || typeof values !== 'object') {
            return;
        }
        this._suppressQuestionRulesRefresh = true;
        Object.entries(values).forEach(([id, value]) => {
            const $el = $(`#questions-content #${CSS.escape(id)}, #phone-wrapper #${CSS.escape(id)}`);
            if (!$el.length) {
                return;
            }
            $el.val(value);
            if ($el.hasClass('select2-hidden-accessible')) {
                $el.trigger('change.select2');
            }
            if ($el.hasClass('landing-embed-option-value')) {
                const $group = $el.closest('.control').find('.landing-embed-option-buttons');
                $group.find('.landing-embed-option-btn').removeClass('is-selected');
                $group.find('.landing-embed-option-btn').filter(function () {
                    return String($(this).attr('data-value')) === String(value);
                }).addClass('is-selected');
            }
        });
        this._suppressQuestionRulesRefresh = false;
    }

    triggerChangeForNewlyPreselectedCountryFields(preservedValues) {
        const preserved = preservedValues && typeof preservedValues === 'object' ? preservedValues : {};
        $('#questions-content select[data-country-select="true"]').each((_, el) => {
            const $el = $(el);
            const id = $el.attr('id');
            if (!id || !$el.val() || preserved[id]) {
                return;
            }
            $el.trigger('change');
        });
    }

    collectAnswersForQuestionRules() {
        const answers = {};
        const nationality = $('#nationality').val();
        if (nationality) {
            answers.nationality = nationality;
        }
        const destination = $('#destination').val();
        if (destination) {
            answers.destination = destination;
        }

        // City, visa type and visa expiration can drive rules of their own
        Object.assign(answers, collectOnshoreFieldAnswers({
            city: $('#city').val(),
            visaType: $('#visa_type').val(),
            visaMonth: $('#visa_month').val(),
            visaYear: $('#visa_year').val(),
        }));

        const primaryIds = new Set(['fullname', 'email', 'phone', 'nationality', 'city', 'destination', 'visa_type', 'visa_month', 'visa_year', 'how_continue']);
        $('#questions-content').find('input, select, textarea').each(function () {
            const $el = $(this);
            const id = $el.attr('id');
            if (!id || primaryIds.has(id) || $el.is(':disabled')) {
                return;
            }
            if (!/^\d+$/.test(String(id))) {
                return;
            }
            const val = $el.val();
            if (val !== null && val !== undefined && val !== '') {
                answers[id] = val;
            }
        });

        return answers;
    }

    isIgnoredPrimaryFieldForRules(el) {
        if (!el) {
            return false;
        }
        const id = el.id || '';
        const name = el.getAttribute('name') || '';
        const ignored = new Set(['fullname', 'email', 'phone', 'inter-phone']);
        if (ignored.has(id) || ignored.has(name)) {
            return true;
        }
        const type = (el.type || '').toLowerCase();
        return type === 'tel' || type === 'email' || type === 'password';
    }

    setupQuestionRulesWatcher() {
        if (this._questionRulesWatcherBound) {
            return;
        }
        this._questionRulesWatcherBound = true;

        // Only `change` on rule-driving fields. Never remount while typing phone/email.
        $(document).on('change.landingQuestionRules', '#questions-content select, #questions-content input, #questions-content textarea, #phone-wrapper input', (event) => {
            if (!this.questionsRefreshEnabled || this._suppressQuestionRulesRefresh) {
                return;
            }
            if (this.isIgnoredPrimaryFieldForRules(event.target)) {
                return;
            }
            // Without Question Rules only the Student Type answer can change the questions
            if (!this.questionsRulesEnabled && !this._studentTypeQuestionIds.has(String(event.target.id))) {
                return;
            }
            this.refreshQuestionsForRules();
        });
    }

    refreshQuestionsForRules() {
        if (this.wizardMainComplete) {
            return Promise.resolve(false);
        }
        const run = this.loadQuestions({
            preserveValues: true,
            answers: this.collectAnswersForQuestionRules(),
        });
        this._rulesRefreshPromise = run;
        return run.then(
            (result) => {
                if (this._rulesRefreshPromise !== run) {
                    return this._rulesRefreshPromise;
                }
                this._rulesRefreshPromise = null;
                return result;
            },
            () => {
                if (this._rulesRefreshPromise !== run) {
                    return this._rulesRefreshPromise;
                }
                this._rulesRefreshPromise = null;
                return false;
            }
        );
    }

    refreshQuestionsForPrefilledAnswers() {
        const answers = this.collectAnswersForQuestionRules();
        if (Object.keys(answers).length === 0) {
            return Promise.resolve(false);
        }
        return this.refreshQuestionsForRules();
    }

    captureWizardStepAnchor() {
        if (this.embedFormLayout !== 'wizard' || !this.wizardStepElements || !this.wizardStepElements.length) {
            return null;
        }
        const $step = $(this.wizardStepElements.get(this.wizardStepIndex));
        if (!$step.length) {
            return null;
        }
        const inputId = $step.find('input[id], select[id], textarea[id]').first().attr('id');
        return inputId || null;
    }

    showErrorMessage(message) {
        $("#questions-content").html('<p>' + message + '</p>').removeClass('hidden');
    }

    renderQuestions(questions) {
        let html = '';
        let hasMultipleDestinations = false;
        this.nonExtraQuestionIds = [];
        this.hasDestination = false;
        this.cityOptionsByDestination = {};

        try {
            // Preprocess city options by destination
            questions.forEach(field => {
                if (field.id === 'city' && Array.isArray(field.options)) {
                    field.options.forEach(opt => {
                        if (opt.destination_id) {
                            if (!this.cityOptionsByDestination[opt.destination_id]) {
                                this.cityOptionsByDestination[opt.destination_id] = [];
                            }
                            this.cityOptionsByDestination[opt.destination_id].push(opt);
                        }
                    });
                }
            });

            questions.forEach(field => {
                if (field.id === 'destination' && Array.isArray(field.options) && field.options.length > 1) {
                    hasMultipleDestinations = true;
                }
            });

            this.hasMultipleDestinationsForCity = hasMultipleDestinations;

            questions.forEach(field => {
                if (field.is_extra) {
                    html += `<div class="extra-question hidden">` + this.renderField(field) + `</div>`;
                    return;
                }

                if (field.id === "phone") {
                    $('#phone-wrapper label.label').html(
                        this.sanitizeQuestionLabelHtml(field.question) + this.escapeHtml(this.wizardRequiredLabelSuffix())
                    );
                    $('#phone-wrapper').find('.landing-embed-field-information').remove();
                    const phoneInfoHtml = this.renderFieldInformation(field);
                    if (phoneInfoHtml) {
                        $('#phone-wrapper label.label').first().after(phoneInfoHtml);
                    }
                    $('#phone-wrapper').removeClass('hidden');
                    html += '<div id="phone-placeholder"></div>';
                    return;
                }
                
                // Check if destination field is present
                if (field.id === 'destination') {
                    this.hasDestination = true;
                }
                
                // Track non-extra marketing questions (excluding primary fields)
                if (!['fullname', 'email', 'phone', 'nationality', 'city', 'destination'].includes(field.id)) {
                    this.nonExtraQuestionIds.push(field.id);
                }

                // For city field, if multiple destinations, render disabled and empty initially
                if (field.id === 'city' && hasMultipleDestinations) {
                    html += this.renderField({ ...field, options: [], disabled: true });
                } else {
                    html += this.renderField(field);
                }
            });

            $("#questions-loader").addClass('hidden');
            $("#questions-content").html(html).removeClass('hidden');
            $("#phone-placeholder").replaceWith($("#phone-wrapper"));
            
            this.setupCalendarTrigger();
            this.setupExtraQuestionsWatcher();
            this.ensureNumberPlaceholders();
            this.ensurePlaceholders();

            if (this.embedFormLayout === 'wizard') {
                this.applyWizardLayout({ resumeAnchor: this._resumeWizardFieldId });
                this._resumeWizardFieldId = null;
                this.initEmbedWizardControls();
            }

            this.initSelect2();

            // Add destination change handler to filter city options
            const destinationSelect = $('#destination');
            const citySelect = $('#city');
            if (destinationSelect.length && citySelect.length && hasMultipleDestinations) {
                citySelect.prop('disabled', true).html(`<option value="">${__('landing.select_one')}</option>`);
                destinationSelect.on('change', () => {
                    this.populateCityOptions(destinationSelect.val());
                });
            }

            this.syncCalendarRegisterSubmitBinding();
            $(document).trigger('questionsLoaded');
        } catch (e) {
            throw e;
        }
    }

    // With several destinations the city list depends on the chosen destination. Also called
    // after a remount that restored an earlier destination answer without firing its change event.
    // `notify` fires `change` so listeners (autosave, rules) see the reset city. A remount passes false:
    // the answers did not change, so it only refreshes the select2 widget and does not re-trigger autosave.
    populateCityOptions(destinationId, selectedCityId = null, notify = true) {
        const $city = $('#city');
        if (!$city.length || !this.hasMultipleDestinationsForCity) {
            return;
        }

        const cities = (destinationId && this.cityOptionsByDestination[destinationId]) || [];
        let cityOptions = `<option value="">${__('landing.select_one')}</option>`;
        cities.forEach(opt => {
            const selected = selectedCityId !== null && String(opt.value) === String(selectedCityId) ? ' selected' : '';
            cityOptions += `<option value="${opt.value}"${selected}>${opt.label}</option>`;
        });

        $city.prop('disabled', cities.length === 0).html(cityOptions).trigger(notify ? 'change' : 'change.select2');
    }

    formatWizardTemplate(str, values) {
        if (!str) return '';
        return str.replace(/:([a-z_]+)/gi, (_, key) => (values[key] !== undefined && values[key] !== null ? String(values[key]) : `:${key}`));
    }

    normalizeFieldOptions(field) {
        const rawOpts = field?.options;
        if (Array.isArray(rawOpts)) {
            return rawOpts;
        }
        if (rawOpts && typeof rawOpts === 'object') {
            return [rawOpts];
        }
        return [];
    }

    wizardRequiredLabelSuffix() {
        return this.embedFormLayout === 'wizard' ? '' : ' *';
    }

    wizardFieldHasValue(val) {
        return val !== null && val !== undefined && String(val).trim() !== '';
    }

    isNonCriteriaFlow() {
        return $(document).data('hasCalendar') !== true;
    }

    shouldBindLoaderRegisterHandler() {
        if (this.wizardSubmitSettled || !$('#form-calendar').length || !$('#btn-register').length) {
            return false;
        }
        if ($(document).data('hasCalendar') === true) {
            return false;
        }
        if (this.embedFormLayout === 'wizard' && !this.wizardMainComplete) {
            return false;
        }
        return true;
    }

    syncCalendarRegisterSubmitBinding() {
        const $register = $('#btn-register');
        if (!$register.length) {
            return;
        }
        if (this.shouldBindLoaderRegisterHandler()) {
            this.ensureCalendarRegisterClickHandler();
            return;
        }
        $register.off('click.landingCalendarRegister');
    }

    readyWizardNav() {
        if (this.embedFormLayout !== 'wizard') {
            return;
        }
        if (this.$wizardNav) {
            this.$wizardNav.removeClass('hidden');
        }
        if (this.$wizardNext) {
            this.$wizardNext.removeClass('hidden is-disabled is-loading');
            this.$wizardNext.prop('disabled', false);
            this.$wizardNext.attr('aria-disabled', 'false');
        }
        this.updateWizardNextState();
    }

    getVisiblePostMainExtraQuestions() {
        return $('#questions-content .extra-question:not(.hidden)');
    }

    getWizardStepFieldElements($step) {
        const $stepEl = $($step);
        if ($stepEl.hasClass('extra-question')) {
            return $stepEl.find('.field .control').find('input, select, textarea');
        }
        const $controls = $stepEl.find('.control');
        if ($controls.length) {
            return $controls.find('input, select, textarea');
        }
        return $stepEl.find('input, select, textarea');
    }

    getWizardFieldValue($el) {
        const $field = $($el);
        if ($field.is('select') && $field.hasClass('select2-hidden-accessible')) {
            const val = $field.val();
            if (this.wizardFieldHasValue(val)) {
                return val;
            }
        }
        return $field.val();
    }

    validateVisiblePostMainExtras(applyUi = true) {
        const $visibleExtras = this.getVisiblePostMainExtraQuestions();
        if (!$visibleExtras.length) {
            return true;
        }
        let allValid = true;
        $visibleExtras.each((_, el) => {
            const $extra = $(el);
            const filled = this.isWizardStepFilled($extra);
            const valid = filled ? this.isWizardStepValid($extra, applyUi) : false;
            if (!filled) {
                this.isWizardStepValid($extra, applyUi);
            }
            if (!filled || !valid) {
                allValid = false;
            }
        });
        return allValid;
    }

    isWizardStepFilled($step) {
        const $stepEl = $($step);
        const phoneEl = $stepEl.find('input[name="phone"]').first()[0];
        if (phoneEl && phoneEl.whatsappValidated === true) {
            return true;
        }
        let ok = true;
        this.getWizardStepFieldElements($stepEl).each((_, el) => {
            const $el = $(el);
            const type = $el.attr('type');
            if (type === 'hidden' && !$el.hasClass('landing-embed-option-value')) {
                return;
            }
            const name = $el.attr('name');
            if (name === '_token') {
                return;
            }
            if ($el.is(':checkbox') || $el.is(':radio')) {
                return;
            }
            if ($el.hasClass('landing-embed-option-value') || $el.hasClass('landing-embed-number-wheel-input')) {
                if (!this.wizardFieldHasValue(this.getWizardFieldValue($el))) {
                    ok = false;
                }
                return;
            }
            if ($el.is('select')) {
                if (!this.wizardFieldHasValue(this.getWizardFieldValue($el))) {
                    ok = false;
                }
                return;
            }
            let val = this.getWizardFieldValue($el);
            if (!this.wizardFieldHasValue(val) && $el.attr('name') === 'phone') {
                const id = $el.attr('id');
                const inter = id ? $stepEl.find(`#inter-${id}`).val() : null;
                val = inter;
            }
            if (!this.wizardFieldHasValue(val)) {
                ok = false;
            }
        });
        return ok;
    }

    isWizardStepValid($step, applyUi = true) {
        const validator = $('#form-calendar').data('validator');
        const rules = validator && validator.settings ? validator.settings.rules : null;
        let ok = true;
        this.getWizardStepFieldElements($($step)).each((_, el) => {
            const $el = $(el);
            const type = $el.attr('type');
            if (type === 'hidden' && !$el.hasClass('landing-embed-option-value')) {
                return;
            }
            const name = $el.attr('name');
            if (name === '_token') {
                return;
            }
            const id = $el.attr('id');

            if ($el.hasClass('landing-embed-option-value') || $el.hasClass('landing-embed-number-wheel-input')) {
                const hasVal = this.wizardFieldHasValue(this.getWizardFieldValue($el));
                if (!hasVal) {
                    ok = false;
                    if (applyUi) {
                        $el.addClass('is-danger');
                        if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                            window.landingEmbedWizardSetLabelInvalid(el, true);
                        }
                    }
                } else if (applyUi) {
                    $el.removeClass('is-danger');
                    if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                        window.landingEmbedWizardSetLabelInvalid(el, false);
                    }
                }
                return;
            }

            if (validator && rules && (rules[name] || rules[id])) {
                // Treat already-validated phone as valid to avoid async re-trigger.
                const isPhoneWhatsappOk = (name === 'phone' || id === 'phone') && el && el.whatsappValidated === true;
                const isOk = isPhoneWhatsappOk ? true : (validator.element(el) !== false);
                if (!isOk) {
                    ok = false;
                }
                if (applyUi && typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                    window.landingEmbedWizardSetLabelInvalid(el, !isOk);
                }
                return;
            }

            if ($el.is('select')) {
                const isOk = this.wizardFieldHasValue(this.getWizardFieldValue($el));
                if (!isOk) ok = false;
                if (applyUi) {
                    $el.toggleClass('is-danger', !isOk);
                    $el.next('.select2-container').toggleClass('is-danger', !isOk);
                    if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                        window.landingEmbedWizardSetLabelInvalid(el, !isOk);
                    }
                }
                return;
            }
            if ($el.is(':checkbox') || $el.is(':radio')) {
                return;
            }
            const val = $el.val();
            const hasVal = !!(val && String(val).trim());
            // Format validation (pattern, type, etc) using HTML constraint validation where available.
            const formatOk = typeof el.checkValidity === 'function' ? el.checkValidity() : true;
            const isOk = hasVal && formatOk;
            if (!isOk) ok = false;
            if (applyUi) {
                $el.toggleClass('is-danger', !isOk);
                if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                    window.landingEmbedWizardSetLabelInvalid(el, !isOk);
                }
            }
        });
        return ok;
    }

    applyWizardLayout(options = {}) {
        const $content = $('#questions-content');
        const $toWrap = $content.children('.phone-wrapper, .field');
        if ($toWrap.length === 0) {
            return;
        }

        $toWrap.wrapAll('<div class="landing-wizard-inner"></div>');
        const $inner = $content.children('.landing-wizard-inner');
        const i18n = this.wizardI18n || {};
        const $progress = $('<div class="landing-wizard-progress" aria-live="polite"></div>');
        const $nav = $('<div class="landing-wizard-nav"></div>');
        $inner.before($progress);
        $content.append($nav);
        $nav.html(
            '<button type="button" class="landing-wizard-prev landing-wizard-nav-btn">' +
                '<span class="landing-wizard-prev-icon" aria-hidden="true">&#8592;</span> <span class="landing-wizard-prev-label"></span>' +
                '</button>' +
                '<button type="button" class="landing-wizard-next landing-wizard-nav-btn landing-wizard-next-primary"><span class="landing-wizard-next-label"></span> <span class="landing-wizard-next-icon" aria-hidden="true">&#8594;</span></button>'
        );

        this.$wizardInner = $inner;
        this.$wizardProgress = $progress;
        this.$wizardNav = $nav;
        this.$wizardPrev = $nav.find('.landing-wizard-prev');
        this.$wizardNext = $nav.find('.landing-wizard-next');
        this.wizardNextAttemptedByStep = {};
        this.wizardMainComplete = false;
        this.wizardFinishMainAllowed = true;
        this.embedHowContinueDeferred = false;
        this.wizardStepElements = $inner.children();
        this.refreshWizardEmbedExtraStepCount();

        const resumeAnchor = options.resumeAnchor || null;
        let resumeIndex = 0;
        if (resumeAnchor) {
            const found = this.wizardStepElements.toArray().findIndex((el) => {
                const $el = $(el);
                if ($el.find(`#${CSS.escape(resumeAnchor)}`).length) {
                    return true;
                }
                return $el.attr('id') === resumeAnchor;
            });
            if (found >= 0) {
                resumeIndex = found;
            }
        }
        this.wizardStepIndex = resumeIndex;

        $nav.find('.landing-wizard-prev-label').text(i18n.previous || __('landing.embed_wizard_previous'));
        $nav.find('.landing-wizard-next-label').text(i18n.continue || __('landing.embed_wizard_continue'));

        $('#questions-wrapper').addClass('landing-wizard-active');

        this.$wizardPrev.off('click.landingWizardNav').on('click.landingWizardNav', () => this.onWizardPrev());
        this.$wizardNext.off('click.landingWizardNav').on('click.landingWizardNav', () => this.onWizardNext());

        this.updateWizardStepVisibility();
        this.wizardNormalizeCurrentStepVisualState();

        this.$wizardInner.off('.landingWizardNextState');
        this.$wizardInner.on('input.landingWizardNextState change.landingWizardNextState', 'input, select, textarea', (e) => {
            this.wizardLiveSyncFieldFromEvent(e);
        });
        this.$wizardInner.on('phoneValidationComplete.landingWizardNextState', 'input[name="phone"]', (e) => {
            this.wizardSyncPhoneValidationState(e.target);
        });
        this.$wizardInner.on('keydown.landingWizardNextState', 'input, select', (e) => {
            if (e.key !== 'Enter') {
                return;
            }
            const $target = $(e.target);
            if ($target.is('textarea') || $target.hasClass('landing-embed-option-value')) {
                return;
            }
            e.preventDefault();
            this.advanceWizardFromKeyboard();
        });

        this.patchWizardValidatorDelegates();
    }

    wizardSyncPhoneValidationState(el) {
        if (this.wizardMainComplete || !this.wizardStepElements || !el) {
            return;
        }
        const $current = $(this.wizardStepElements.get(this.wizardStepIndex));
        if (!$current.length || !$current[0].contains(el)) {
            return;
        }
        this.hideBigSubmitDuringEmbedInnerWizard();
        syncWizardPhoneValidationState(el, this.wizardNextAttemptedByStep, this.wizardStepIndex, {
            onAfterSync: () => this.updateWizardNextState(),
        });
    }

    wizardFieldIsEmpty(element) {
        const $el = $(element);
        if ($el.is('select')) {
            const val = $el.val();
            return !val || val === '';
        }
        const val = $el.val();
        return val === undefined || val === null || String(val).trim() === '';
    }

    wizardShouldDeferJqueryValidateForElement(element) {
        if (!element || !$('#questions-wrapper').hasClass('landing-wizard-active') || this.wizardMainComplete) {
            return false;
        }
        if (!this.wizardStepElements || !this.wizardStepElements.length) {
            return false;
        }
        const $current = $(this.wizardStepElements.get(this.wizardStepIndex));
        if (!$current.length || !$current[0].contains(element)) {
            return false;
        }
        const idx = this.wizardStepIndex;
        if (this.wizardNextAttemptedByStep && this.wizardNextAttemptedByStep[idx]) {
            return false;
        }
        return this.wizardFieldIsEmpty(element);
    }

    wizardClearJqueryValidateHighlight(element) {
        const $el = $(element);
        const fieldId = $el.attr('id');
        const name = ($el.attr('name') || '').replace(/[\[\]]/g, '');
        $el.removeClass('is-danger');
        if ($el.hasClass('select2-hidden-accessible')) {
            $el.next('.select2-container').removeClass('is-danger');
        }
        if (fieldId) {
            $(`#error_${fieldId}`).removeClass('show').empty();
        }
        if (name) {
            $(`#error_${name}`).removeClass('show').empty();
        }
        if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
            window.landingEmbedWizardSetLabelInvalid(element, false);
        }
    }

    wizardResetJqueryValidateDeferredElement(validator, element) {
        if (!validator || !element || !element.name) {
            return;
        }
        if (validator.invalid && Object.prototype.hasOwnProperty.call(validator.invalid, element.name)) {
            delete validator.invalid[element.name];
        }
        if (validator.submitted && Object.prototype.hasOwnProperty.call(validator.submitted, element.name)) {
            delete validator.submitted[element.name];
        }
    }

    patchWizardValidatorDelegates() {
        const validator = $('#form-calendar').data('validator');
        if (!validator || validator.__landingWizardValidateHooked) {
            return;
        }
        validator.__landingWizardValidateHooked = true;
        const loader = this;
        const origOnfocusout = validator.settings.onfocusout;
        const origOnkeyup = validator.settings.onkeyup;

        validator.settings.onfocusout = function (element, event) {
            if (loader.wizardShouldDeferJqueryValidateForElement(element)) {
                loader.wizardClearJqueryValidateHighlight(element);
                loader.wizardResetJqueryValidateDeferredElement(this, element);
                return;
            }
            if (typeof origOnfocusout === 'function') {
                return origOnfocusout.call(this, element, event);
            }
        };

        validator.settings.onkeyup = function (element, event) {
            if (loader.wizardShouldDeferJqueryValidateForElement(element)) {
                loader.wizardClearJqueryValidateHighlight(element);
                loader.wizardResetJqueryValidateDeferredElement(this, element);
                return;
            }
            if (typeof origOnkeyup === 'function') {
                return origOnkeyup.call(this, element, event);
            }
        };
    }

    wizardNormalizeCurrentStepVisualState() {
        if (this.wizardMainComplete || !this.wizardStepElements) {
            return;
        }
        const $current = $(this.wizardStepElements.get(this.wizardStepIndex));
        $current.find('input, select, textarea').each((_, el) => {
            const $el = $(el);
            const type = $el.attr('type');
            if (type === 'hidden' && !$el.hasClass('landing-embed-option-value')) {
                return;
            }
            if ($el.attr('name') === '_token') {
                return;
            }
            $el.removeClass('is-danger');
            if ($el.hasClass('select2-hidden-accessible')) {
                $el.next('.select2-container').removeClass('is-danger');
            }
            if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                window.landingEmbedWizardSetLabelInvalid(el, false);
            }
        });
    }

    wizardLiveSyncFieldFromEvent(e) {
        if (this.wizardMainComplete || !this.wizardStepElements) {
            return;
        }
        let el = e.target;
        if (el && el.closest && el.closest('.iti')) {
            const tel = el.closest('.iti').querySelector('input[name]:not([type="hidden"])');
            if (tel) {
                el = tel;
            }
        }
        const $current = $(this.wizardStepElements.get(this.wizardStepIndex));
        if (!$current.length || !el || !$current[0].contains(el)) {
            return;
        }

        this.updateWizardNextState();
        this.hideBigSubmitDuringEmbedInnerWizard();
        if (el && el.getAttribute && el.getAttribute('name') === 'phone') {
            // Defer one tick so intl-tel-input UI updates first.
            setTimeout(() => this.wizardSyncPhoneValidationState(el), 0);
        }

        const validator = $('#form-calendar').data('validator');
        const rules = validator && validator.settings ? validator.settings.rules : null;
        const $el = $(el);
        const type = $el.attr('type');
        const name = $el.attr('name');
        const id = $el.attr('id');
        const nextAttempted = !!(this.wizardNextAttemptedByStep && this.wizardNextAttemptedByStep[this.wizardStepIndex]);

        if (name === '_token') {
            return;
        }
        if (type === 'hidden' && !$el.hasClass('landing-embed-option-value')) {
            return;
        }

        const clearUi = () => {
            $el.removeClass('is-danger');
            if ($el.hasClass('select2-hidden-accessible')) {
                $el.next('.select2-container').removeClass('is-danger');
            }
            if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                window.landingEmbedWizardSetLabelInvalid(el, false);
            }
        };
        const setInvalidUi = () => {
            $el.addClass('is-danger');
            if ($el.hasClass('select2-hidden-accessible')) {
                $el.next('.select2-container').addClass('is-danger');
            }
            if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                window.landingEmbedWizardSetLabelInvalid(el, true);
            }
        };

        if ($el.hasClass('landing-embed-option-value')) {
            const val = $el.val();
            const hasVal = val !== null && val !== undefined && String(val).trim() !== '';
            if (!hasVal) {
                if (!nextAttempted) {
                    clearUi();
                }
                return;
            }
            if (hasVal) {
                clearUi();
            }
            return;
        }

        if (validator && rules && (rules[name] || rules[id])) {
            const raw = $el.val();
            const hasVal = raw !== undefined && raw !== null && String(raw).trim() !== '';
            if (!hasVal) {
                if (!nextAttempted) {
                    clearUi();
                }
                return;
            }

            const isOk = validator.element(el) !== false;
            if (isOk) clearUi();
            else setInvalidUi();
            return;
        }

        if ($el.is('select')) {
            const val = $el.val();
            const hasVal = !!(val && val !== '');
            if (hasVal) clearUi();
            else if (!nextAttempted) clearUi();
            return;
        }
        if ($el.is(':checkbox') || $el.is(':radio')) {
            return;
        }
        const val = $el.val();
        const hasVal = !!(val && String(val).trim());
        const formatOk = typeof el.checkValidity === 'function' ? el.checkValidity() : true;
        if (!hasVal) {
            if (!nextAttempted) {
                clearUi();
            }
            return;
        }
        if (hasVal && formatOk) {
            clearUi();
            return;
        }
        setInvalidUi();
    }

    updateWizardNextState() {
        if (!this.$wizardNext) {
            return;
        }
        if (this.wizardMainComplete) {
            if (this.embedFormLayout === 'wizard') {
                this.$wizardNext.prop('disabled', false);
                this.$wizardNext.removeClass('is-disabled');
                this.$wizardNext.attr('aria-disabled', 'false');
            }
            return;
        }
        if (!this.wizardStepElements) {
            return;
        }
        const $current = $(this.wizardStepElements.get(this.wizardStepIndex));
        const isFilled = this.isWizardStepFilled($current);
        let canContinue = isFilled;
        let shouldBlockClick = false;

        // Gate Continue on phone async status when present.
        const phoneEl = $current.find('input[name="phone"]').first()[0];
        if (canContinue && phoneEl && phoneEl.whatsappValidated !== true) {
            const wrapper = phoneEl.validationWrapper;
            const loaderEl = wrapper ? wrapper.loader : null;
            const isChecking = !!phoneEl.pendingRequest || !!(loaderEl && !loaderEl.classList.contains('hide'));
            if (isChecking) {
                canContinue = false;
                shouldBlockClick = true;
            }
        }

        // Only block clicks during async WhatsApp checks; otherwise allow clicks to surface validation errors.
        this.$wizardNext.prop('disabled', shouldBlockClick);
        this.$wizardNext.toggleClass('is-disabled', shouldBlockClick);
        this.$wizardNext.attr('aria-disabled', shouldBlockClick ? 'true' : 'false');
    }

    isWizardSubmitButtonVisible() {
        return $('#submit').filter(':visible').length > 0;
    }

    isNonCriteriaWizardWithoutPostMainExtras() {
        return (
            this.embedFormLayout === 'wizard'
            && this.wizardMainComplete
            && $(document).data('hasCalendar') === false
            && !$('#questions-wrapper').hasClass('landing-wizard-how-continue-pending')
            && (this.wizardEmbedExtraStepCount || 0) === 0
        );
    }

    isWizardPostMainExtraFinalStep() {
        if (this.embedFormLayout !== 'wizard' || !this.wizardMainComplete) {
            return false;
        }
        if (this.embedHowContinueDeferred) {
            return false;
        }
        if ($('#questions-wrapper').hasClass('landing-wizard-how-continue-pending')) {
            return false;
        }
        if ($(document).data('hasCalendar') !== false) {
            return false;
        }
        const $visibleExtras = this.getVisiblePostMainExtraQuestions();
        if (!$visibleExtras.length) {
            return false;
        }
        let hasEligible = false;
        $visibleExtras.each((_, el) => {
            if (this.isExtraQuestionEligibleForEmbedWizard($(el))) {
                hasEligible = true;
            }
        });
        return hasEligible;
    }

    shouldWizardNextActAsSubmit() {
        if (this.embedFormLayout !== 'wizard' || !this.$wizardNext || this.$wizardNext.hasClass('hidden')) {
            return false;
        }
        if (this.embedHowContinueDeferred) {
            return false;
        }
        if (!this.wizardMainComplete) {
            return false;
        }
        if (this.hasPendingWizardPostMainSteps()) {
            return false;
        }
        if (!this.validateVisiblePostMainExtras(false)) {
            return false;
        }
        const howVal = String($('#how_continue').val() || '').trim();
        if (howVal === 'whatsapp') {
            return true;
        }
        if ($(document).data('hasCalendar') === true) {
            return false;
        }
        return true;
    }

    syncWizardNavSubmitId() {
        if (!this.$wizardNext) {
            return;
        }
        if (this.shouldWizardNextActAsSubmit() || this.isWizardPostMainExtraFinalStep()) {
            this.$wizardNext.attr('id', 'submit');
        } else {
            this.$wizardNext.removeAttr('id');
        }
    }

    syncWizardNav() {
        if (!this.$wizardNext) {
            return;
        }
        const $label = this.$wizardNext.find('.landing-wizard-next-label');
        if (!$label.length) {
            return;
        }
        const i18n = this.wizardI18n || {};
        const submitLabel = i18n.submit || i18n.continue || __('landing.embed_wizard_submit');
        const continueLabel = i18n.continue || __('landing.embed_wizard_continue');

        if (this.embedHowContinueDeferred) {
            $label.text(submitLabel);
            this.$wizardNext.attr('aria-label', submitLabel);
            this.syncWizardNavSubmitId();
            return;
        }

        if (this.wizardMainComplete) {
            if (this.embedFormLayout === 'wizard') {
                const useSubmit = this.shouldWizardNextActAsSubmit() || this.isWizardPostMainExtraFinalStep();
                const label = useSubmit ? submitLabel : continueLabel;
                $label.text(label);
                this.$wizardNext.attr('aria-label', label);
            }
            this.syncWizardNavSubmitId();
            return;
        }

        const innerSteps = this.wizardStepElements ? this.wizardStepElements.length : 0;
        const idx = this.wizardStepIndex;
        const extra = this.wizardEmbedExtraStepCount || 0;
        const isLastInnerDomStep = innerSteps > 0 && idx === innerSteps - 1;
        // Last inner step shows Submit only when no extra segments follow (e.g. how_continue).
        const useSubmit = isLastInnerDomStep && extra === 0;
        const label = useSubmit ? submitLabel : continueLabel;
        $label.text(label);
        this.$wizardNext.attr('aria-label', label);
        this.syncWizardNavSubmitId();
    }

    hideBigSubmitDuringEmbedInnerWizard() {
        if (this.embedFormLayout !== 'wizard') {
            return;
        }
        const hasInner = $('#questions-content .landing-wizard-inner').length > 0;
        if (!hasInner) {
            return;
        }
        const inQuestionsPhase =
            $('#questions-wrapper').hasClass('landing-wizard-active')
            || $('#questions-wrapper').hasClass('landing-wizard-how-continue-pending');
        if (!inQuestionsPhase) {
            return;
        }
        $('#btn-submit-wrapper').addClass('hidden');
    }

    // Move nav below the calendar and hide the next button.
    hideWizardNavForCalendar() {
        if (this.embedFormLayout !== 'wizard') {
            return;
        }
        if (this.$wizardNav) {
            this.$wizardNav.find('.landing-wizard-next').addClass('hidden');
            const $calendar = $('#calendar-wrapper');
            if ($calendar.length && !$calendar.next().is(this.$wizardNav)) {
                this.$wizardNav.detach().insertAfter($calendar);
            }
            this.$wizardNav.removeClass('hidden').addClass('landing-wizard-nav--below-calendar');
        }
        if (this.$wizardProgress) {
            this.$wizardProgress.removeClass('hidden');
        }
        $('#btn-submit-wrapper').addClass('hidden');
    }

    restoreWizardNavToQuestionsContent() {
        if (!this.$wizardNav) {
            return;
        }
        this.$wizardNav.removeClass('landing-wizard-nav--below-calendar');
        this.readyWizardNav();
        const $questionsContent = $('#questions-content');
        if ($questionsContent.length && !this.$wizardNav.parent().is($questionsContent)) {
            this.$wizardNav.detach().appendTo($questionsContent);
        }
    }

    // Restore nav to its default slot when calendar closes.
    restoreWizardNavNoCalendar() {
        if (this.embedFormLayout !== 'wizard') {
            return;
        }
        if (!$('#calendar-wrapper').hasClass('hidden')) {
            return;
        }
        const inHowContinuePhase =
            $('#questions-wrapper').hasClass('landing-wizard-how-continue-pending')
            || (this.wizardMainComplete && $('#how_continue').closest('.extra-question:not(.hidden)').length);
        if (!inHowContinuePhase) {
            return;
        }
        this.restoreWizardNavToQuestionsContent();
        if (this.$wizardNav) {
            this.$wizardNav.removeClass('hidden');
        }
        if (this.$wizardProgress) {
            this.$wizardProgress.removeClass('hidden');
        }
        $('#btn-submit-wrapper').addClass('hidden');
        if (String($('#how_continue').val() || '').trim() === 'whatsapp') {
            $('#btn-register').prop('disabled', false);
        }
        if (this.$wizardPrev) {
            this.$wizardPrev.prop('disabled', false);
            this.$wizardPrev.removeClass('is-disabled');
            this.$wizardPrev.css('visibility', '');
        }
        this.readyWizardNav();
        this.syncWizardNav();
    }

    lockWizardInnerStepsHidden() {
        if (!this.wizardStepElements || !this.wizardStepElements.length) {
            return;
        }
        this.wizardStepElements.addClass('hidden');
    }

    hasPendingWizardPostMainSteps() {
        if (this.embedHowContinueDeferred) {
            return true;
        }
        if ($('#questions-wrapper').hasClass('landing-wizard-how-continue-pending')) {
            return true;
        }
        const hasCalendar = $(document).data('hasCalendar');
        if (hasCalendar === undefined) {
            return true;
        }
        if (hasCalendar === true && !$('#calendar-wrapper').hasClass('hidden')) {
            return true;
        }

        const $visibleExtras = this.getVisiblePostMainExtraQuestions();
        if (!$visibleExtras.length) {
            return false;
        }

        let pending = false;
        $visibleExtras.each((_, el) => {
            const $extra = $(el);
            const $how = $extra.find('#how_continue');
            if ($how.length) {
                const howVal = String($how.val() || '').trim();
                if (!howVal) {
                    pending = true;
                }
                return;
            }
            if (!this.isWizardStepFilled($extra)) {
                pending = true;
            }
        });

        return pending;
    }

    markWizardSubmitInFlight() {
        if (this.wizardSubmitSettled || this._wizardSubmitInFlight) {
            return false;
        }
        this._wizardSubmitInFlight = true;
        return true;
    }

    clearWizardSubmitInFlight() {
        this._wizardSubmitInFlight = false;
    }

    shouldAutoSubmitAfterMainWizard() {
        if (this.embedFormLayout !== 'wizard' || !this.wizardMainComplete || this.wizardSubmitSettled) {
            return false;
        }
        if (this._wizardSubmitInFlight) {
            return false;
        }
        if ($(document).data('hasCalendar') === undefined) {
            return false;
        }
        if (this.hasPendingWizardPostMainSteps()) {
            return false;
        }
        if (!this.isWizardSubmitButtonVisible()) {
            return false;
        }
        if (!this.validateVisiblePostMainExtras(false)) {
            return false;
        }
        const $register = $('#btn-register');
        return $register.length > 0 && !$register.prop('disabled');
    }

    maybeAutoSubmitAfterMainWizard() {
        if (!this.shouldAutoSubmitAfterMainWizard()) {
            return;
        }
        this.wizardFinalSubmit();
    }

    updateWizardStepVisibility() {
        if (!this.wizardStepElements || !this.wizardStepElements.length || this.wizardMainComplete) {
            return;
        }
        const innerSteps = this.wizardStepElements.length;
        const total = innerSteps + (this.wizardEmbedExtraStepCount || 0);
        const idx = this.wizardStepIndex;
        this.wizardStepElements.each((i, el) => {
            $(el).toggleClass('hidden', i !== idx);
        });

        const pct = total ? Math.round(((idx + 1) / total) * 100) : 0;
        const i18n = this.wizardI18n || {};
        const left = this.formatWizardTemplate(i18n.stepOf || __('landing.embed_wizard_step_of'), {
            current: idx + 1,
            total,
        });
        const right = this.formatWizardTemplate(i18n.percentComplete || __('landing.embed_wizard_percent_complete'), { percent: pct });

        this.$wizardProgress.html(
            `<div class="landing-wizard-progress-meta"><span class="landing-wizard-progress-left">${left}</span>` +
                `<span class="landing-wizard-progress-right">${right}</span></div>` +
                `<div class="landing-wizard-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}">` +
                `<div class="landing-wizard-progress-fill" style="width:${pct}%"></div></div>`
        );

        this.$wizardPrev.prop('disabled', idx <= 0);
        this.$wizardPrev.toggleClass('is-disabled', idx <= 0);
        this.$wizardPrev.css('visibility', idx <= 0 ? 'hidden' : '');
        this.updateWizardNextState();
        this.syncWizardNav();
        this.hideBigSubmitDuringEmbedInnerWizard();
        const $activeStep = $(this.wizardStepElements.get(idx));
        this.syncOptionButtonSelectionState($activeStep);
        this.syncVisibleNumberWheels($activeStep);
        focusFirstFieldInWizardStep($activeStep);
    }

    async onWizardPrev() {
        if (this.wizardPrevBusy) {
            return;
        }

        const isReverting =
            this.embedHowContinueDeferred ||
            (this.wizardMainComplete &&
                this.embedFormLayout === 'wizard' &&
                (this.hasPendingWizardPostMainSteps() || this.getVisiblePostMainExtraQuestions().length > 0));

        if (isReverting) {
            this.wizardPrevBusy = true;
            this.wizardNextBusy = true; // Block any auto-advance

            // Capture height to avoid layout jump
            const $formBox = $('.form-box');
            const currentHeight = $formBox.outerHeight();
            if (currentHeight > 100) {
                $formBox.css('min-height', currentHeight + 'px');
            }

            // Show standard questions-loader and hide content as requested
            $('#questions-loader').removeClass('hidden');
            $('#questions-content').addClass('hidden');
            $('.extra-question').addClass('hidden');

            if (this.$wizardPrev) {
                this.$wizardPrev.addClass('is-loading');
            }

            // Apply submitting class to hide nav/progress via CSS
            $('.form-wrapper').addClass('landing-wizard-submitting');

            try {
                // Wait for any pending autosaves to ensure criteria is updated
                await awaitLandingAutosaveForWizardAdvance();

                // Quick loader delay to prevent immediate re-selection while state is settling
                await new Promise((r) => setTimeout(r, 600));

                // Ensure we have fresh criteria/step counts before showing the main wizard again
                this.refreshWizardEmbedExtraStepCount();

                this.revertWizardFromDeferredHowContinue();
            } finally {
                $('#questions-loader').addClass('hidden');
                $('#questions-content').removeClass('hidden');
                $formBox.css('min-height', '');

                $('#landing-embed-submit-loader').addClass('hidden');
                $('.form-wrapper').removeClass('landing-wizard-submitting');
                if (this.$wizardPrev) {
                    this.$wizardPrev.removeClass('is-loading');
                }
                this.wizardPrevBusy = false;
                this.wizardNextBusy = false;
            }
            return;
        }

        if (this.wizardMainComplete || this.wizardStepIndex <= 0) {
            return;
        }
        if (Array.isArray(this._wizardVisitStack) && this._wizardVisitStack.length) {
            while (this._wizardVisitStack.length) {
                const prevAnchor = this._wizardVisitStack.pop();
                const found = this.findWizardStepIndexByFieldId(prevAnchor);
                if (found >= 0) {
                    this.wizardStepIndex = found;
                    this.updateWizardStepVisibility();
                    return;
                }
            }
        }
        this.wizardStepIndex--;
        this.updateWizardStepVisibility();
    }

    findWizardStepIndexByFieldId(fieldId) {
        if (!fieldId || !this.wizardStepElements || !this.wizardStepElements.length) {
            return -1;
        }
        return this.wizardStepElements.toArray().findIndex((el) => {
            const $el = $(el);
            if ($el.find(`#${CSS.escape(fieldId)}`).length) {
                return true;
            }
            return $el.attr('id') === fieldId;
        });
    }

    advanceWizardFromKeyboard() {
        if (this.wizardNextBusy || this.wizardMainComplete) {
            return;
        }
        if (this.$wizardNext && this.$wizardNext.hasClass('hidden')) {
            return;
        }
        this.onWizardNext();
    }

    revertWizardFromDeferredHowContinue() {
        this.embedHowContinueDeferred = false;
        $('#questions-wrapper').removeClass('landing-wizard-how-continue-pending');
        this.wizardMainComplete = false;
        $('.extra-question').addClass('hidden');
        if (this.$wizardInner && this.$wizardInner.length) {
            this.$wizardInner.removeClass('hidden');
        }
        const max = this.wizardStepElements ? this.wizardStepElements.length - 1 : 0;
        this.wizardStepIndex = Math.max(0, max);
        $('#questions-wrapper').addClass('landing-wizard-active');
        if (this.$wizardProgress) {
            this.$wizardProgress.removeClass('hidden');
        }
        const $cal = $('#calendar-wrapper');
        if ($cal.length) {
            $cal.addClass('hidden');
            $cal.detach().insertAfter($('#questions-wrapper'));
        }
        this.restoreWizardNavToQuestionsContent();
        $('#btn-submit-wrapper').addClass('hidden');
        this.updateWizardStepVisibility();
        this.wizardNormalizeCurrentStepVisualState();
        this.updateWizardNextState();
        if (this.$wizardNav) {
            this.$wizardNav.removeClass('hidden');
        }
    }

    async wizardHowContinueNext() {
        const $how = $('#how_continue');
        const $field = $how.closest('.field');
        const $extra = $how.closest('.extra-question');
        const $step = $field.length ? $field : $extra;
        if (!$how.length) {
            this.embedHowContinueDeferred = false;
            $('#questions-wrapper').removeClass('landing-wizard-how-continue-pending');
            $('#calendar-wrapper').removeClass('hidden');
            $(document).trigger('calendarShow');
            this.hideWizardNavForCalendar();
            return;
        }
        if (!this.isWizardStepFilled($step)) {
            this.isWizardStepValid($step, true);
            return;
        }
        if (!this.isWizardStepValid($step, true)) {
            return;
        }
        const howVal = String($how.val() || '').trim();
        $how.trigger('change');

        // Save now, before we hide the field below — otherwise the save fires after
        // the field is hidden and the value gets dropped.
        await awaitLandingAutosaveForWizardAdvance();

        if (howVal === 'whatsapp') {
            if (typeof window.landingEmbedRestoreCalendarWrapperToDefaultSlot === 'function') {
                window.landingEmbedRestoreCalendarWrapperToDefaultSlot();
            }
            this.embedHowContinueDeferred = false;
            $('#questions-wrapper').removeClass('landing-wizard-how-continue-pending');
            this.wizardFinalSubmit();
            return;
        }

        if ($extra.length) {
            $extra.addClass('hidden');
        }
        if (typeof window.landingEmbedRestoreCalendarWrapperToDefaultSlot === 'function') {
            window.landingEmbedRestoreCalendarWrapperToDefaultSlot();
        }
        if (!$('#calendar-wrapper').hasClass('hidden')) {
            this.hideWizardNavForCalendar();
        } else {
            this.restoreWizardNavNoCalendar();
        }
        this.embedHowContinueDeferred = false;
        $('#questions-wrapper').removeClass('landing-wizard-how-continue-pending');
        if ($('#calendar-wrapper').hasClass('hidden')) {
            this.scheduleExtraQuestions();
        }
    }

    ensureCalendarRegisterClickHandler() {
        const $register = $('#btn-register');
        if (!$register.length) {
            return;
        }
        $register.off('click.landingCalendarRegister');
        $register.on('click.landingCalendarRegister', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.handleNonCriteriaSubmit();
        });
    }

    wizardFinalSubmit() {
        if (this.wizardSubmitSettled || this._wizardSubmitInFlight) {
            return;
        }

        const howContinueVal = String($('#how_continue').val() || '').trim();

        if (
            this.embedFormLayout === 'wizard'
            && howContinueVal !== 'whatsapp'
            && !this.isWizardSubmitButtonVisible()
        ) {
            return;
        }

        if (
            this.embedFormLayout === 'wizard'
            && this.wizardMainComplete
            && (this.hasPendingWizardPostMainSteps() || !this.validateVisiblePostMainExtras(false))
        ) {
            return;
        }
        const $register = $('#btn-register');

        if (this.isNonCriteriaFlow()) {
            this.ensureCalendarRegisterClickHandler();
            if (!this.markWizardSubmitInFlight()) {
                return;
            }
            this.handleNonCriteriaSubmit();
            return;
        }

        if (howContinueVal === 'whatsapp' && $register.length) {
            $register.prop('disabled', false).removeClass('is-loading');
            let hasWhatsappHandler = false;
            const clickHandlers = $._data($register[0], 'events')?.click || [];
            hasWhatsappHandler = clickHandlers.some((handler) => handler.namespace === 'landingWhatsapp');
            if (!hasWhatsappHandler) {
                $('#how_continue').trigger('change');
                const afterBind = $._data($register[0], 'events')?.click || [];
                hasWhatsappHandler = afterBind.some((handler) => handler.namespace === 'landingWhatsapp');
            }
            if (hasWhatsappHandler) {
                if (!this.markWizardSubmitInFlight()) {
                    return;
                }
                $register.trigger('click');
                return;
            }
        }

        const calendarHidden = $('#calendar-wrapper').hasClass('hidden');
        const shouldUseRegisterDone =
            calendarHidden && !this.hasPendingWizardPostMainSteps() && howContinueVal !== 'whatsapp';

        if (shouldUseRegisterDone) {
            this.ensureCalendarRegisterClickHandler();
            if (!this.markWizardSubmitInFlight()) {
                return;
            }
            this.handleNonCriteriaSubmit();
            return;
        }

        if ($register.length) {
            if (!this.markWizardSubmitInFlight()) {
                return;
            }
            $register.prop('disabled', false).removeClass('is-loading');
            $register.trigger('click');
        }
    }

    async onWizardNextAfterMainComplete() {
        const hasCalendar = $(document).data('hasCalendar');

        if (hasCalendar === false) {
            if (!this.validateVisiblePostMainExtras(true)) {
                return;
            }
            this.wizardFinalSubmit();
            return;
        }

        const $howExtra = $('#how_continue').closest('.extra-question:not(.hidden)');
        if ($howExtra.length) {
            if (!this.isWizardStepFilled($howExtra)) {
                this.isWizardStepValid($howExtra, true);
                return;
            }
            if (!this.isWizardStepValid($howExtra, true)) {
                return;
            }
            await this.wizardHowContinueNext();
            if (!$('#calendar-wrapper').hasClass('hidden')) {
                return;
            }
        }
        if ($(document).data('hasCalendar') === true && $('#calendar-wrapper').hasClass('hidden')) {
            this.revealCalendarWrapper();
            this.hideWizardNavForCalendar();
            this.syncWizardNav();
            return;
        }
        if (this.isWizardSubmitButtonVisible()) {
            this.wizardFinalSubmit();
        }
    }

    async onWizardNext(isAutoAdvance = false) {
        if (this.wizardNextBusy) {
            return;
        }
        if (this.embedHowContinueDeferred) {
            this.wizardNextBusy = true;
            try {
                await this.wizardHowContinueNext();
                if (
                    this.embedFormLayout === 'wizard'
                    && $('#calendar-wrapper').hasClass('hidden')
                    && this.isWizardSubmitButtonVisible()
                ) {
                    this.wizardFinalSubmit();
                }
            } finally {
                this.wizardNextBusy = false;
                if (this.$wizardNext) {
                    this.$wizardNext.removeClass('is-loading');
                }
            }
            return;
        }
        if (this.wizardMainComplete) {
            if (this.embedFormLayout === 'wizard') {
                this.wizardNextBusy = true;
                try {
                    await this.onWizardNextAfterMainComplete();
                } finally {
                    this.wizardNextBusy = false;
                    if (this.$wizardNext) {
                        this.$wizardNext.removeClass('is-loading');
                    }
                }
            }
            return;
        }
        this.wizardNextBusy = true;
        try {
            const $steps = this.wizardStepElements;
            const max = $steps.length - 1;
            const $current = $($steps.get(this.wizardStepIndex));
            if (this.wizardNextAttemptedByStep) {
                this.wizardNextAttemptedByStep[this.wizardStepIndex] = true;
            }
            if (!this.isWizardStepFilled($current)) {
                this.isWizardStepValid($current, true);
                return;
            }

            // Wait out the phone async-validation debounce.
            const $phone = $current.find('input[name="phone"]').first();
            if ($phone.length && $phone[0] && $phone[0].validationTimer && !$phone[0].pendingRequest) {
                await new Promise((r) => setTimeout(r, 650));
            }

            const pending = [];
            $current.find('input, select, textarea').each((_, el) => {
                const p = el && el.pendingRequest;
                if (p && typeof p.then === 'function') {
                    pending.push(p);
                }
            });
            const showAdvanceLoader = !!(this.$wizardNext && (pending.length || window.__landingAutosave));
            if (showAdvanceLoader) {
                this.$wizardNext.addClass('is-loading');
            }
            if (pending.length) {
                await Promise.allSettled(pending);
            }

            let stepValid = this.isWizardStepValid($current, true);
            if (!stepValid && $phone.length && $phone[0] && $phone[0].whatsappValidated === true) {
                stepValid = true;
            }
            if (!stepValid) {
                if (showAdvanceLoader) {
                    this.$wizardNext.removeClass('is-loading');
                }
                return;
            }

            const autosaveOk = await awaitLandingAutosaveForWizardAdvance();
            if (!autosaveOk) {
                if (showAdvanceLoader) {
                    this.$wizardNext.removeClass('is-loading');
                }
                return;
            }

            this.refreshWizardEmbedExtraStepCount();

            if (this.wizardStepIndex < max) {
                const currentAnchor = this.captureWizardStepAnchor();
                if (currentAnchor) {
                    if (!Array.isArray(this._wizardVisitStack)) {
                        this._wizardVisitStack = [];
                    }
                    this._wizardVisitStack.push(currentAnchor);
                }
                this.wizardStepIndex++;
                if (this.wizardStepIndex === max) {
                    this.wizardFinishMainAllowed = false;
                    setTimeout(() => {
                        this.wizardFinishMainAllowed = true;
                    }, 0);
                }
                if (this.wizardNextAttemptedByStep) {
                    this.wizardNextAttemptedByStep[this.wizardStepIndex] = true;
                }
                this.updateWizardStepVisibility();
                this.wizardNormalizeCurrentStepVisualState();
                return;
            }

            if (isAutoAdvance && (this.wizardEmbedExtraStepCount || 0) <= 0) {
                if (showAdvanceLoader) {
                    this.$wizardNext.removeClass('is-loading');
                }
                return;
            }

            if (!this.wizardFinishMainAllowed) {
                if (showAdvanceLoader) {
                    this.$wizardNext.removeClass('is-loading');
                }
                return;
            }
            this.finishMainWizard();
        } finally {
            this.wizardNextBusy = false;
            if (this.$wizardNext) {
                this.$wizardNext.removeClass('is-loading');
            }
        }
    }

    finishMainWizard() {
        const hasCalendar = $(document).data('hasCalendar');
        const howContinueEl = $('#how_continue');
        const howContinueRaw = howContinueEl.length ? howContinueEl.val() : '';
        const howContinueVal =
            howContinueRaw !== null && howContinueRaw !== undefined ? String(howContinueRaw).trim() : '';

        const wizardCalendarDeferHowContinue =
            this.embedFormLayout === 'wizard'
            && hasCalendar !== false
            && howContinueVal !== 'whatsapp'
            && howContinueEl.length > 0
            && howContinueVal === '';

        this.wizardMainComplete = true;
        this.embedHowContinueDeferred = wizardCalendarDeferHowContinue;
        this.refreshWizardEmbedExtraStepCount();
        this.lockWizardInnerStepsHidden();

        if (this.$wizardInner && this.$wizardInner.length) {
            this.$wizardInner.addClass('hidden');
        }
        $('#questions-wrapper').removeClass('landing-wizard-active');

        if (wizardCalendarDeferHowContinue) {
            $('#questions-wrapper').addClass('landing-wizard-how-continue-pending');
            if (this.$wizardNav) {
                this.$wizardNav.removeClass('hidden');
            }
            $('#btn-submit-wrapper').addClass('hidden');
            $('#calendar-wrapper').addClass('hidden');
            if (this.$wizardPrev) {
                this.$wizardPrev.prop('disabled', false);
                this.$wizardPrev.removeClass('is-disabled');
            }
            this.syncWizardNav();
        } else {
            this.embedHowContinueDeferred = false;
            $('#questions-wrapper').removeClass('landing-wizard-how-continue-pending');

            if (this.embedFormLayout === 'wizard') {
                $('#btn-submit-wrapper').addClass('hidden');
                if (hasCalendar === true && howContinueVal !== 'whatsapp') {
                    if (this.$wizardProgress) {
                        this.$wizardProgress.removeClass('hidden');
                    }
                    if (this.$wizardNav) {
                        this.$wizardNav.addClass('hidden');
                    }
                    $('#calendar-wrapper').removeClass('hidden');
                    $(document).trigger('calendarShow');
                    this.hideWizardNavForCalendar();
                } else {
                    $('#calendar-wrapper').addClass('hidden');
                    if (typeof window.landingEmbedRestoreCalendarWrapperToDefaultSlot === 'function') {
                        window.landingEmbedRestoreCalendarWrapperToDefaultSlot();
                    }
                    if (!this.isNonCriteriaWizardWithoutPostMainExtras()) {
                        if (this.$wizardProgress) {
                            this.$wizardProgress.removeClass('hidden');
                        }
                        if (this.$wizardNav) {
                            this.$wizardNav.removeClass('hidden');
                        }
                        if (this.$wizardPrev) {
                            this.$wizardPrev.prop('disabled', false);
                            this.$wizardPrev.removeClass('is-disabled');
                            this.$wizardPrev.css('visibility', '');
                        }
                    }
                    $('#btn-register').prop('disabled', false);
                    if (hasCalendar === false) {
                        this.syncCalendarRegisterSubmitBinding();
                        if (!this.isNonCriteriaWizardWithoutPostMainExtras()) {
                            this.readyWizardNav();
                        }
                    }
                }
                this.syncWizardNav();
            } else {
                if (this.$wizardProgress) {
                    this.$wizardProgress.addClass('hidden');
                }
                if (this.$wizardNav) {
                    this.$wizardNav.addClass('hidden');
                }
                if (hasCalendar === true && howContinueVal !== 'whatsapp') {
                    $('#btn-submit-wrapper').addClass('hidden');
                    $('#calendar-wrapper').removeClass('hidden');
                    $(document).trigger('calendarShow');
                } else {
                    $('#calendar-wrapper').addClass('hidden');
                    $('#btn-submit-wrapper').removeClass('hidden');
                    $('#btn-register').prop('disabled', false);
                    if (hasCalendar === false) {
                        this.syncCalendarRegisterSubmitBinding();
                    }
                }
            }
        }

        if (wizardCalendarDeferHowContinue) {
            const $extraHow = $('#how_continue').closest('.extra-question');
            const $cal = $('#calendar-wrapper');
            if ($extraHow.length && $cal.length) {
                $cal.detach().insertAfter($extraHow);
            }
        }
        $(document).trigger('questionsLoaded');
        this.scheduleExtraQuestions();
    }

    // Is this extra question eligible to be a wizard step?
    isExtraQuestionEligibleForEmbedWizard($extraEl) {
        const hasCalendar = $(document).data('hasCalendar');
        const requireExtraQuestions = $(document).data('requireExtraQuestions');
        const questionField = $extraEl.find('[data-calendar]');
        const isCalendarQuestion = questionField.length > 0 && questionField.data('calendar') === true;
        if (hasCalendar === true) {
            return isCalendarQuestion;
        }
        if (hasCalendar === false) {
            if (requireExtraQuestions === false) {
                return false;
            }
            return !isCalendarQuestion;
        }
        return false;
    }

    refreshWizardEmbedExtraStepCount() {
        if (this.embedFormLayout !== 'wizard') {
            this.wizardEmbedExtraStepCount = 0;
            return;
        }
        const countPendingExtras =
            $(document).data('requireExtraQuestions') === true
            || $(document).data('hasCalendar') === true;
        const $extras = countPendingExtras
            ? $('#questions-content').children('.extra-question')
            : $('#questions-content').children('.extra-question:not(.hidden)');
        let n = 0;
        $extras.each((_, el) => {
            if (this.isExtraQuestionEligibleForEmbedWizard($(el))) {
                n++;
            }
        });
        this.wizardEmbedExtraStepCount = n;
    }

    maybeRefreshWizardProgressForExtraCount() {
        if (
            this.embedFormLayout === 'wizard'
            && !this.wizardMainComplete
            && this.wizardStepElements
            && this.wizardStepElements.length
        ) {
            this.updateWizardStepVisibility();
        }
    }

    // Keep the progress bar in sync on the extra-question segment after inner steps.
    syncEmbedWizardProgressForPostInnerExtras() {
        if (this.embedFormLayout !== 'wizard' || !this.$wizardProgress || !this.wizardStepElements || !this.wizardStepElements.length) {
            return;
        }
        if (!this.wizardMainComplete) {
            return;
        }
        if (!$('#questions-content .landing-wizard-inner').length) {
            return;
        }
        if (!$('#calendar-wrapper').hasClass('hidden')) {
            return;
        }

        this.refreshWizardEmbedExtraStepCount();
        const innerSteps = this.wizardStepElements.length;
        const extra = this.wizardEmbedExtraStepCount || 0;
        const total = innerSteps + extra;
        if (total === 0 || extra === 0) {
            return;
        }

        const visibleEligibleExtra = $('#questions-content').children('.extra-question').filter((_, el) => {
            const $el = $(el);
            return !$el.hasClass('hidden') && this.isExtraQuestionEligibleForEmbedWizard($el);
        });

        if (!visibleEligibleExtra.length && !this.embedHowContinueDeferred) {
            return;
        }

        const current = total;
        const pct = 100;
        const i18n = this.wizardI18n || {};
        const left = this.formatWizardTemplate(i18n.stepOf || __('landing.embed_wizard_step_of'), {
            current,
            total,
        });
        const right = this.formatWizardTemplate(i18n.percentComplete || __('landing.embed_wizard_percent_complete'), { percent: pct });

        this.$wizardProgress.html(
            `<div class="landing-wizard-progress-meta"><span class="landing-wizard-progress-left">${left}</span>` +
                `<span class="landing-wizard-progress-right">${right}</span></div>` +
                `<div class="landing-wizard-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}">` +
                `<div class="landing-wizard-progress-fill" style="width:${pct}%"></div></div>`
        );
        this.$wizardProgress.removeClass('hidden');
    }

    scheduleExtraQuestions() {
        if (this._extraVisibilityRafId != null) {
            cancelAnimationFrame(this._extraVisibilityRafId);
        }
        this._extraVisibilityRafId = requestAnimationFrame(() => {
            this._extraVisibilityRafId = null;
            this.checkAndShowExtraQuestions();
        });
    }

    checkAndShowExtraQuestions() {
        if ($(document).data('autosaveTriggered') !== true && !this.wizardMainComplete) {
            return;
        }

        this.refreshWizardEmbedExtraStepCount();

        try {
        // Keep extra blocks hidden until the main wizard is done
        if (
            this.embedFormLayout === 'wizard'
            && $('#questions-wrapper').hasClass('landing-wizard-active')
            && !this.wizardMainComplete
        ) {
            $('.extra-question').addClass('hidden');
            return;
        }

        const baseFields = ['fullname', 'email', 'phone', 'nationality', 'city'];
        if (this.hasDestination) {
            baseFields.push('destination');
        }
        
        const baseFieldsAnswered = baseFields.every(id => {
            const element = $(`#${id}`);

            if (element.length === 0) {
                return true;
            }

            if (element.is('select')) {
                return element.val();
            } else if (element.is(':checkbox')) {
                return element.is(':checked');
            } else if (element.is(':radio')) {
                return $(`input[name="${id}"]:checked`).length > 0;
            }

            return element.val();
        });

        const marketingFieldsAnswered = this.nonExtraQuestionIds.every(id => {
            const element = $(`#${id}`);
            if (element.length === 0) {
                return true;
            }

            if (element.is('select')) {
                return element.val();
            } else if (element.is(':checkbox')) {
                return element.is(':checked');
            } else if (element.is(':radio')) {
                return $(`input[name="${id}"]:checked`).length > 0;
            }

            return element.val();
        });

        if (baseFieldsAnswered && marketingFieldsAnswered) {
            const hasCalendar = $(document).data('hasCalendar');
            const requireExtraQuestions = $(document).data('requireExtraQuestions');

            if (this.wizardMainComplete && hasCalendar === false) {
                this.embedHowContinueDeferred = false;
                $('#questions-wrapper').removeClass('landing-wizard-how-continue-pending');
            }

            const howContinuePending =
                hasCalendar !== false
                && (this.embedHowContinueDeferred
                    || $('#questions-wrapper').hasClass('landing-wizard-how-continue-pending'));
            const $howContinueExtra = $('#how_continue').closest('.extra-question');

            $('.extra-question').addClass('hidden');

            if (howContinuePending && $howContinueExtra.length) {
                $howContinueExtra.removeClass('hidden');
            } else if (hasCalendar === true) {
                $('.extra-question').each(function() {
                    const questionField = $(this).find('[data-calendar]');
                    const isCalendarQuestion = questionField.length > 0 && questionField.data('calendar') === true;
                    $(this).toggleClass('hidden', !isCalendarQuestion);
                });
            } else if (hasCalendar === false) {
                if (requireExtraQuestions !== false) {
                    $('.extra-question').each(function() {
                        const questionField = $(this).find('[data-calendar]');
                        const isCalendarQuestion = questionField.length > 0 && questionField.data('calendar') === true;
                        $(this).toggleClass('hidden', isCalendarQuestion);
                    });
                }
            }

            $('.extra-question:not(.hidden) select.select2').each(function() {
                if (!$(this).data('select2')) $(this).select2();
            });
        } else {
            $('.extra-question').addClass('hidden');
        }
        } finally {
            this.maybeRefreshWizardProgressForExtraCount();
            this.syncEmbedWizardProgressForPostInnerExtras();
            if (this.embedFormLayout === 'wizard' && this.wizardMainComplete) {
                if (this.isNonCriteriaWizardWithoutPostMainExtras()) {
                    this.wizardFinalSubmit();
                } else {
                    this.readyWizardNav();
                    this.syncWizardNav();
                    if ($(document).data('hasCalendar') === true && $('#calendar-wrapper').hasClass('hidden')) {
                        this.restoreWizardNavNoCalendar();
                    }
                }
            }
            this.syncCalendarRegisterSubmitBinding();
            if (this.embedFormLayout !== 'wizard' && $(document).data('hasCalendar') !== true) {
                window.__landingAutosave?.updateSubmitButtonState?.();
            }
        }
    }

    escapeHtml(str) {
        if (str === null || str === undefined) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    // Force type="button" on CMS label <button> elements so they don't submit the form.
    sanitizeQuestionLabelHtml(str) {
        let s = str === null || str === undefined ? '' : String(str);
        if (this.embedFormLayout === 'wizard') {
            s = s.replace(/\s*\*+\s*$/u, '').trim();
        }
        if (s === '' || !s.includes('<')) {
            return this.escapeHtml(s);
        }
        try {
            const wrap = document.createElement('div');
            wrap.innerHTML = s;
            wrap.querySelectorAll('button:not([type])').forEach((btn) => {
                btn.setAttribute('type', 'button');
            });
            if (this.embedFormLayout === 'wizard') {
                wrap.querySelectorAll('.has-information, [data-target^="modal-info-"]').forEach((el) => {
                    el.remove();
                });
            }
            return wrap.innerHTML;
        } catch (e) {
            return this.escapeHtml(s);
        }
    }

    renderWizardNumberField(field) {
        const opts = this.normalizeFieldOptions(field);
        const minOpt = opts.find((o) => o && o.min !== undefined && o.min !== null);
        const maxOpt = opts.find((o) => o && o.max !== undefined && o.max !== null);
        const allowDecimal = opts.some((o) => o && o.allow_decimal === true);
        if (allowDecimal) {
            return null;
        }
        let rawMin = minOpt ? minOpt.min : null;
        const minMissingOrZero = rawMin === null || rawMin === undefined || rawMin === '' || Number(rawMin) === 0;
        if (minMissingOrZero) {
            rawMin = 0;
        }
        if (rawMin === null || rawMin === undefined || rawMin === '') {
            return null;
        }
        if (!maxOpt) {
            return null;
        }
        const minN = parseInt(String(rawMin), 10);
        const maxN = parseInt(String(maxOpt.max), 10);
        if (Number.isNaN(minN) || Number.isNaN(maxN) || minN > maxN) {
            return null;
        }
        if (maxN - minN > 250) {
            return null;
        }

        const defaultValue = minMissingOrZero && 17 >= minN && 17 <= maxN ? 17 : null;
        const defaultIndex = defaultValue !== null ? defaultValue - minN : null;

        const itemHeight = 44;
        const viewportH = 220;
        const pad = Math.max(0, Math.floor((viewportH - itemHeight) / 2));
        const values = [];
        for (let n = minN; n <= maxN; n++) {
            values.push(n);
        }

        const listItems = values.map((n) => (
            `<li class="landing-embed-number-wheel-item" data-value="${n}" role="option"><span class="landing-embed-number-wheel-label">${n}</span></li>`
        )).join('');

        const descriptionHtml = this.renderFieldDescription(field);
        const informationHtml = this.renderFieldInformation(field);
        const fieldTemplate = (inputHtml) => `
            <div class="field landing-embed-field landing-embed-field--number">
                <label class="label" for="${field.id}">${this.sanitizeQuestionLabelHtml(field.question)}${this.escapeHtml(this.wizardRequiredLabelSuffix())}</label>
                ${informationHtml}
                ${descriptionHtml}
                <div class="control">${inputHtml}</div>
                <div class="help is-danger" id="error_${field.id}"></div>
            </div>`;

        const calendarAttr = field.calendar ? ` data-calendar="true"` : '';
        const baseAttrs = `name="${field.id}" id="${field.id}"${calendarAttr}`;

        const defaultIndexAttr = defaultIndex !== null ? ` data-default-index="${defaultIndex}"` : '';
        return fieldTemplate(`
            <div class="landing-embed-number-wheel" data-min="${minN}" data-max="${maxN}" data-item-height="${itemHeight}"${defaultIndexAttr}>
                <div class="landing-embed-number-wheel-viewport" tabindex="0" role="listbox" aria-label="${this.escapeHtml(field.question)}">
                    <ul class="landing-embed-number-wheel-list" style="padding-top:${pad}px;padding-bottom:${pad}px">
                        ${listItems}
                    </ul>
                </div>
                <input type="text" readonly class="input number-input landing-embed-number-wheel-input" ${baseAttrs}
                    data-min="${minN}" data-max="${maxN}"
                    pattern="^[0-9]+$"
                    autocomplete="off" aria-hidden="true" />
            </div>`);
    }

    // Wizard button options (grid/stacked); returns null to fall back to select/select2.
    renderWizardOptionField(field) {
        if (field.id === 'nationality' || field.type === 'country') {
            return null;
        }
        if (field.id === 'city' && (field.disabled || this.hasMultipleDestinationsForCity)) {
            return null;
        }
        if (!field.options?.length) {
            return null;
        }
        const MAX_BUTTON_OPTIONS = 24;
        if (field.options.length > MAX_BUTTON_OPTIONS) {
            return null;
        }

        const labels = field.options.map(o => String(o.label ?? '').length);
        const maxLabelLen = Math.max(...labels, 0);
        const useGrid = maxLabelLen <= 22 && field.options.length <= 9;

        const buttonsHtml = field.options.map((opt) => {
            let label = opt.label;
            if (field.id === 'visa_month') {
                const monthIndex = parseInt(opt.value, 10) - 1;
                if (monthIndex >= 0 && monthIndex <= 11) {
                    const monthName = new Date(2024, monthIndex, 1).toLocaleDateString(this.questionsApiLocale, { month: 'long' });
                    label = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                }
            }
            const val = this.escapeHtml(opt.value);
            const cc = opt.country_code
                ? ` data-country-code="${this.escapeHtml(opt.country_code)}"`
                : '';
            const flagHtml = opt.country_code
                ? `<span class="nationality-flag-icon iti__flag iti__${String(opt.country_code).toLowerCase()}"></span> `
                : '';
            const cleanLab = stripLeadingFlagChars(label);
            return `<button type="button" class="landing-embed-option-btn" data-value="${val}"${cc}>${flagHtml}${this.escapeHtml(cleanLab)}</button>`;
        }).join('');

        const gridMod = useGrid ? ' landing-embed-option-buttons--grid' : ' landing-embed-option-buttons--stack';

        const descriptionHtml = this.renderFieldDescription(field);
        const informationHtml = this.renderFieldInformation(field);
        const fieldTemplate = (inputHtml) => `
            <div class="field landing-embed-field landing-embed-field--option">
                <label class="label" for="${field.id}">${this.sanitizeQuestionLabelHtml(field.question)}${this.escapeHtml(this.wizardRequiredLabelSuffix())}</label>
                ${informationHtml}
                ${descriptionHtml}
                <div class="control">${inputHtml}</div>
                <div class="help is-danger" id="error_${field.id}"></div>
            </div>`;

        const hiddenCalendar = field.calendar ? ' data-calendar="true"' : '';

        return fieldTemplate(`
            <input type="hidden" class="landing-embed-option-value" name="${field.id}" id="${field.id}" value=""${hiddenCalendar} />
            <div class="landing-embed-option-buttons${gridMod}" role="group">${buttonsHtml}</div>`);
    }

    initEmbedWizardControls() {
        this.initLandingEmbedNumberWheels();
        this.initLandingEmbedOptionButtons();
        this.decorateOptionButtonFlags();
    }

    decorateOptionButtonFlags() {
        $('.landing-embed-option-btn[data-country-code]').each(function () {
            const $btn = $(this);
            if ($btn.find('.nationality-flag-icon').length) {
                return;
            }
            const cc = String($btn.attr('data-country-code') || '').toLowerCase();
            if (!cc) {
                return;
            }
            $btn.prepend($('<span class="nationality-flag-icon iti__flag iti__' + cc + '"></span>'));
        });
    }

    initLandingEmbedNumberWheels() {
        $('.landing-embed-number-wheel').each(function () {
            const $wheel = $(this);
            const $vp = $wheel.find('.landing-embed-number-wheel-viewport');
            const $input = $wheel.find('input.landing-embed-number-wheel-input');
            if (!$vp.length || !$input.length) {
                return;
            }

            const itemH = parseInt($wheel.data('item-height'), 10) || 44;
            const $items = $wheel.find('.landing-embed-number-wheel-item');

            const scrollToIndex = (idx, animate) => {
                if (idx < 0 || idx >= $items.length) {
                    return;
                }
                const top = idx * itemH;
                if (animate) {
                    $vp.stop().animate({ scrollTop: top }, 120);
                } else {
                    $vp.scrollTop(top);
                }
                const val = String($items.eq(idx).data('value'));
                if ($input.val() !== val) {
                    $input.val(val).trigger('change');
                }
            };

            const indexFromScroll = () => {
                const st = $vp.scrollTop();
                let idx = Math.round(st / itemH);
                idx = Math.max(0, Math.min(idx, $items.length - 1));
                return idx;
            };

            let scrollTimer = null;
            let suppressScrollUntil = 0;
            $vp.off('scroll.landingEmbedWheel').on('scroll.landingEmbedWheel', () => {
                if (Date.now() < suppressScrollUntil) {
                    return;
                }
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                    const idx = indexFromScroll();
                    const val = String($items.eq(idx).data('value'));
                    $items.removeClass('is-active');
                    $items.eq(idx).addClass('is-active');
                    if ($input.val() !== val) {
                        $input.val(val).trigger('change');
                    }
                }, 50);
            });

            $vp.off('keydown.landingEmbedWheel').on('keydown.landingEmbedWheel', (e) => {
                const cur = indexFromScroll();
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                    e.preventDefault();
                    scrollToIndex(Math.min(cur + 1, $items.length - 1), true);
                } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    scrollToIndex(Math.max(cur - 1, 0), true);
                }
            });

            const rawDefaultIdx = parseInt($wheel.data('default-index'), 10);
            const hasDefaultIdx = !Number.isNaN(rawDefaultIdx) && rawDefaultIdx >= 0 && rawDefaultIdx < $items.length;
            const startIdx = hasDefaultIdx ? rawDefaultIdx : 0;
            const startVal = String($items.eq(startIdx).data('value'));
            if (!$input.val() && hasDefaultIdx) {
                $input.val(startVal);
            }

            // Suppress the debounced scroll handler and disable scroll-snap during
            // programmatic scrolls so they can't overwrite the active item back to 0.
            const applyScroll = (idx) => {
                const vpEl = $vp.get(0);
                if (!vpEl) {
                    return;
                }
                suppressScrollUntil = Date.now() + 500;
                const prevSnap = vpEl.style.scrollSnapType;
                vpEl.style.scrollSnapType = 'none';
                const top = idx * itemH;
                vpEl.scrollTop = top;
                window.requestAnimationFrame(() => {
                    vpEl.scrollTop = top;
                    window.requestAnimationFrame(() => {
                        vpEl.scrollTop = top;
                        vpEl.style.scrollSnapType = prevSnap || '';
                    });
                });
            };

            applyScroll(startIdx);
            $items.eq(startIdx).addClass('is-active');

            this._landingEmbedWheelSync = () => {
                const val = $input.val();
                let idx = hasDefaultIdx ? rawDefaultIdx : 0;
                if (val !== undefined && val !== null && val !== '') {
                    const found = $items.toArray().findIndex((it) => String($(it).data('value')) === String(val));
                    if (found >= 0) {
                        idx = found;
                    }
                }
                applyScroll(idx);
                const itemVal = String($items.eq(idx).data('value'));
                if ($input.val() !== itemVal) {
                    $input.val(itemVal);
                }
                $items.removeClass('is-active');
                $items.eq(idx).addClass('is-active');
            };

            if ('IntersectionObserver' in window) {
                const io = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && typeof entry.target._landingEmbedWheelSync === 'function') {
                            entry.target._landingEmbedWheelSync();
                        }
                    });
                }, { threshold: 0.1 });
                io.observe(this);
            }
        });
    }

    syncVisibleNumberWheels($scope) {
        const $root = $scope && $scope.length ? $scope : $(document);
        const run = () => {
            $root.find('.landing-embed-number-wheel').each(function () {
                if (typeof this._landingEmbedWheelSync === 'function') {
                    this._landingEmbedWheelSync();
                }
            });
        };
        // Run now and again after layout so the scroll position settles.
        run();
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(run);
        }
    }

    initLandingEmbedOptionButtons() {
        const self = this;
        $(document).off('click.landingEmbedOpt').on('click.landingEmbedOpt', '.landing-embed-option-btn', async function (e) {
            e.preventDefault();
            const $btn = $(this);
            const $group = $btn.closest('.landing-embed-option-buttons');
            const $control = $btn.closest('.control');
            const $hidden = $control.find('input.landing-embed-option-value');
            if (!$hidden.length) {
                return;
            }
            const val = $btn.attr('data-value');
            $group.find('.landing-embed-option-btn').removeClass('is-selected');
            $btn.addClass('is-selected');

            const oldVal = $hidden.val();
            const nextVal = val === undefined || val === null ? '' : String(val);
            $hidden.val(nextVal).trigger('change');

            if (self.embedFormLayout !== 'wizard' || self.wizardNextBusy || self.wizardMainComplete) {
                return;
            }
            if ($btn.closest('.extra-question').length) {
                return;
            }
            const stepEl = self.wizardStepElements ? self.wizardStepElements.get(self.wizardStepIndex) : null;
            if (!stepEl || !stepEl.contains($hidden.get(0))) {
                return;
            }
            if (oldVal === nextVal) {
                return;
            }

            await new Promise((resolve) => {
                requestAnimationFrame(() => requestAnimationFrame(resolve));
            });
            if (self.questionsRulesEnabled) {
                if (self._rulesRefreshPromise) {
                    await self._rulesRefreshPromise;
                } else {
                    await self.refreshQuestionsForRules();
                }
            }
            if (self.wizardNextBusy || self.wizardMainComplete) {
                return;
            }
            self.onWizardNext(true);
        });
    }

    renderField(field) {
        const descriptionHtml = this.renderFieldDescription(field);
        const informationHtml = this.renderFieldInformation(field);
        if (this.embedFormLayout === 'wizard') {
            if (field.type === 'number') {
                const w = this.renderWizardNumberField(field);
                if (w !== null) {
                    return w;
                }
            }
            if (field.type === 'option' || field.type === 'select') {
                const w = this.renderWizardOptionField(field);
                if (w !== null) {
                    return w;
                }
            }
        }

        const fieldTemplate = (inputHtml) => `
            <div class="field">
                <label class="label" for="${field.id}">${this.sanitizeQuestionLabelHtml(field.question)}${this.escapeHtml(this.wizardRequiredLabelSuffix())}</label>
                ${informationHtml}
                ${descriptionHtml}
                <div class="control">${inputHtml}</div>
                <div class="help is-danger" id="error_${field.id}"></div>
            </div>`;

        const calendarAttr = field.calendar ? ` data-calendar="true"` : '';
        const placeholder = __('landing.input_placeholder');
        const baseAttrs = `name="${field.id}" id="${field.id}"${calendarAttr}`;

        switch(field.type) {
            case "text": 
                return fieldTemplate(`<input type="text" class="input" ${baseAttrs} placeholder="${placeholder}">`);
            
            case "number": {
                const opts = this.normalizeFieldOptions(field);
                let min = opts.find((o) => o && o.min !== undefined && o.min !== null)?.min;
                const max = opts.find((o) => o && o.max !== undefined && o.max !== null)?.max;
                const minMissingOrZero = min === undefined || min === null || min === '' || Number(min) === 0;
                if (minMissingOrZero) {
                    min = 0;
                }
                const allowDecimal = opts.some((o) => o && o.allow_decimal === true);
                const attrs = [
                    `type="text"`,
                    `inputmode="${allowDecimal ? 'decimal' : 'numeric'}"`,
                    `class="input number-input"`,
                    baseAttrs,
                    `pattern="${allowDecimal ? '^[0-9]+(\\.[0-9]+)?$' : '^[0-9]+$'}"`,
                    min !== undefined && min !== null && min !== '' ? `data-min="${min}"` : '',
                    max !== undefined && max !== null && max !== '' ? `data-max="${max}"` : '',
                    `placeholder="${placeholder}"`
                ].filter(Boolean).join(' ');
                return fieldTemplate(`<input ${attrs}>`);
            }
            
            case "textarea":
                return fieldTemplate(`<textarea class="textarea" ${baseAttrs} placeholder="${placeholder}"></textarea>`);
            
            case "option":
            case "select":
            case "country": {
                if (field.id === 'city' && (field.disabled || !field.options?.length)) {
                    return fieldTemplate(`
                        <div class="select is-fullwidth">
                            <select ${baseAttrs} class="select2" disabled>
                                <option value="">${__('landing.select_one')}</option>
                            </select>
                        </div>`);
                }

                const preservedValue = this._preservingFieldValues
                    ? this._preservedFieldValues?.[field.id]
                    : null;
                const hasPreservedValue = preservedValue !== null
                    && preservedValue !== undefined
                    && preservedValue !== '';
                // Preselect by IP when this field has no restored answer (including
                // newly visible country fields after a rules remount).
                const shouldPreselect = !hasPreservedValue
                    && (field.id === 'nationality'
                    || (field.type === 'country' && field.preselect_by_ip));
                const isCountrySelect = field.id === 'nationality' || field.type === 'country';

                const options = field.options?.map(opt => {
                    if (field.id === 'visa_month') {
                        const monthIndex = parseInt(opt.value) - 1;
                        if (monthIndex >= 0 && monthIndex <= 11) {
                            const monthName = new Date(2024, monthIndex, 1).toLocaleDateString(this.questionsApiLocale, { month: 'long' });
                            return `<option value="${opt.value}">${monthName.charAt(0).toUpperCase() + monthName.slice(1)}</option>`;
                        }
                    }
                    let selected = '';
                    if (shouldPreselect) {
                        const optionCountryCode = opt.country_code
                            ? String(opt.country_code).toLowerCase()
                            : '';
                        if (this.countryCode && optionCountryCode && optionCountryCode === this.countryCode) {
                            selected = ' selected';
                        } else if (this.countryName && opt.name && String(opt.name) === String(this.countryName)) {
                            selected = ' selected';
                        }
                    }
                    const countryCodeAttr = (isCountrySelect && opt.country_code) ? ` data-country-code="${opt.country_code}"` : '';
                    return `<option value="${opt.value}"${countryCodeAttr}${selected}>${opt.label}</option>`;
                }).join('') || '';

                const attrs = [
                    baseAttrs,
                    'class="select2"',
                    isCountrySelect ? 'data-country-select="true"' : '',
                    isCountrySelect ? `data-search-placeholder="${__('landing.search_nationality')}"` : '',
                    isCountrySelect ? `data-no-results="${__('landing.no_nationalities')}"` : ''
                ].filter(Boolean).join(' ');

                return fieldTemplate(`
                    <div class="select is-fullwidth">
                        <select ${attrs}>
                            <option value="">${__('landing.select_one')}</option>
                            ${options}
                        </select>
                    </div>`);
            }
            
            default: 
                return '';
        }
    }

    renderFieldInformation(field) {
        if (this.embedFormLayout !== 'wizard') {
            return '';
        }
        const raw = field.information ?? '';
        const text = raw === null || raw === undefined ? '' : String(raw).trim();
        if (!text) {
            return '';
        }
        return `<div class="landing-embed-field-information">${this.sanitizeQuestionLabelHtml(text)}</div>`;
    }

    renderFieldDescription(field) {
        const raw =
            field.description ??
            field.info ??
            field.helper_text ??
            field.help_text ??
            field.subtitle ??
            '';
        const text = raw === null || raw === undefined ? '' : String(raw).trim();
        if (!text) {
            return '';
        }
        return `<div class="landing-embed-field-description">${this.escapeHtml(text)}</div>`;
    }

    initSelect2() {
        $.fn.select2.defaults.set('language', {
            noResults: () => __('landing.no_results')
        });
        
        
        // Initialize select2
        // `select.select2` only: select2 containers also carry the `select2` class, and
        // fields that survive a rules remount (e.g. the calendar timezone) would be re-initialized.
        $('select.select2').not('.no-search, .no-auto-init').each(function () {
            const $el = $(this);

            const noResultsText = $el.data('no-results') || null;
            const options = {};

            if (noResultsText) {
                options.language = {
                    noResults: function() {
                        return noResultsText;
                    }
                };
            }

            if (!$el.data('select2')) {
                $el.select2(options);
            }
        });

        applyCountrySelect2Templates({ template: formatCountrySelect2Option });

        $('.select2').each(function() {
            const container = $(this).next(".select2-container");
            if ($(this).hasClass("is-danger")) {
                container.addClass("is-danger");
            }
        });

        // Change handler for select2 elements
        $('.select2').on("change", function() {
            $(this).next(".select2-container").toggleClass("is-danger", !$(this).val());
        });

        $(document).off('select2:open.select2Placeholder').on('select2:open.select2Placeholder', function(e) {
            const $target = $(e.target);
            const placeholder = $target.data('search-placeholder') || __('landing.type_to_search');
            $('.select2-search__field').attr('placeholder', placeholder);
        });
    }

    revealCalendarWrapper({ triggerShow = true } = {}) {
        const $wrap = $('#calendar-wrapper');
        const wasHidden = $wrap.hasClass('hidden');
        $wrap.removeClass('hidden');
        if (triggerShow && wasHidden) {
            $(document).trigger('calendarShow');
        }
    }

    setupCalendarTrigger() {
        const isEmbedWizard = this.embedFormLayout === 'wizard';

        $(document).off('change.landingEmbedCalendarData', '[data-calendar="true"]').on('change.landingEmbedCalendarData', '[data-calendar="true"]', (e) => {
            const $field = $(e.currentTarget);
            if (isEmbedWizard) {
                const el = e.currentTarget;
                const rawVal = $field.val();
                const value = rawVal !== null && rawVal !== undefined ? String(rawVal).trim() : '';
                const rawHow = $('#how_continue').val();
                const howContinueVal =
                    rawHow !== null && rawHow !== undefined ? String(rawHow).trim() : '';
                const isHowContinue = el.id === 'how_continue' || el.name === 'how_continue';

                if (!value) {
                    $('#calendar-wrapper').addClass('hidden');
                    if (isHowContinue) {
                        this.restoreWizardNavNoCalendar();
                    }
                    return;
                }

                if (!isHowContinue && howContinueVal === '') {
                    return;
                }

                if (howContinueVal === 'whatsapp') {
                    $('#calendar-wrapper').addClass('hidden');
                    if (typeof window.landingEmbedRestoreCalendarWrapperToDefaultSlot === 'function') {
                        window.landingEmbedRestoreCalendarWrapperToDefaultSlot();
                    }
                    this.restoreWizardNavNoCalendar();
                    return;
                }

                if (howContinueVal === '') {
                    return;
                }

                this.revealCalendarWrapper();
                this.hideWizardNavForCalendar();
                return;
            }

            const value = $field.val();
            const howContinueVal = $('#how_continue').val();

            if (value) {
                if (howContinueVal !== 'whatsapp') {
                    this.revealCalendarWrapper();
                } else {
                    $('#calendar-wrapper').addClass('hidden');
                }
            } else {
                $('#calendar-wrapper').addClass('hidden');
            }
        });
    }

    setupExtraQuestionsWatcher() {
        // Combine base fields and marketing questions for watching
        const baseFields = ['fullname', 'email', 'phone', 'nationality', 'city'];
        if (this.hasDestination) {
            baseFields.push('destination');
        }
        const allFieldsToWatch = [...baseFields, ...this.nonExtraQuestionIds];
        
        if (allFieldsToWatch.length === 0) {
            return;
        }

        const selectors = allFieldsToWatch.map(id => `#${id}`).join(', ');
        $(document).on('change', selectors, () => this.scheduleExtraQuestions());
        $(document).on('autosaveTriggered', () => this.scheduleExtraQuestions());

        $(document).on('hasCalendarChanged', (e, hasCalendar) => {
            this.scheduleExtraQuestions();

            const inWizardEmbed =
                this.embedFormLayout === 'wizard'
                && $('#questions-content .landing-wizard-inner').length > 0;
            if (inWizardEmbed) {
                $('#btn-submit-wrapper').addClass('hidden');
            }

            if (hasCalendar === false) {
                $('#calendar-wrapper').addClass('hidden');
                $('#selected_date').val('');
                $('#selected_time').val('');
                $('.calendar-date-btn, .calendar-time-btn').removeClass('active');

                $('#btn-register').off('click.landingWhatsapp');
                this.syncCalendarRegisterSubmitBinding();

                if (inWizardEmbed) {
                    this.restoreWizardNavToQuestionsContent();
                    this.readyWizardNav();
                } else {
                    $('#btn-submit-wrapper').removeClass('hidden');
                }
                $('#btn-register').prop('disabled', false);
            } else if (inWizardEmbed && hasCalendar === true && $('#calendar-wrapper').hasClass('hidden')) {
                this.restoreWizardNavNoCalendar();
            }
        });
    }

    initFormValidation() {
        // Match autosave's email validation.
        $.validator.addMethod("strictEmail", function(value, element) {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            return this.optional(element) || emailRegex.test(value);
        }, __('needValidEmailDomain'));

        const validator = $("#form-calendar").validate({
            errorClass: "is-danger",
            validClass: "",
            rules: {
                phone: {
                    isValidWhatsapp: true
                },
                email: {
                    required: true,
                    strictEmail: true
                }
            },
            errorPlacement: function(error, element) {
                const name = element.attr('name').replace(/[\[\]]/g, '');
                if (this && this.settings && this.settings.__landingWizardSuppressErrors === true) {
                    // Suppress jQuery Validate's error elements in wizard mode.
                    if (typeof this.errorsFor === 'function') {
                        this.errorsFor(element).remove();
                    }
                    if (error && typeof error.remove === 'function') {
                        error.remove();
                    }
                    if (name) {
                        $(`#${name}-error`).remove();
                    }
                    const id = element.attr('id');
                    if (id) {
                        $(`#${id}-error`).remove();
                    }
                    $('#error_' + name).removeClass('show').empty();
                    return;
                }
                if (element.hasClass("select2-hidden-accessible")) {
                    element.next(".select2-container").addClass("is-danger").after(error);
                }
                $('#error_' + name).html(error).addClass('show');
            },
            success: function(label, element) {
                // Remove error styling when field becomes valid
                const fieldId = $(element).attr('id');
                if (fieldId) {
                    $(`#error_${fieldId}`).removeClass('show').empty();
                    $(element).removeClass('is-danger');
                    
                    // Handle Select2
                    if ($(element).hasClass('select2-hidden-accessible')) {
                        $(element).next('.select2-container').removeClass('is-danger');
                    }
                }
                if (this && this.settings && this.settings.__landingWizardSuppressErrors === true) {
                    if (typeof window.landingEmbedWizardSetLabelInvalid === 'function') {
                        window.landingEmbedWizardSetLabelInvalid(element, false);
                    }
                }
            },
            submitHandler: form => {
                $('#btn-register').addClass('is-loading');
                form.submit();
            }
        });
        validator.settings.__landingWizardSuppressErrors = (this.embedFormLayout === 'wizard');
        if (this.embedFormLayout === 'wizard') {
            validator.settings.errorElement = 'span';
        }
    }

    ensureWizardLabelInvalidHelper() {
        setupWizardLabelInvalidHelper();
    }

    initLanguageSwitcher() {
        $('.language-option').on('click', function () {
            const lang = $(this).data('lang');
            const baseUrl = window.location.origin;
            const newPath = window.location.pathname.replace(/^\/[a-z]{2}\//, '/');
            window.location.href = baseUrl + '/' + lang + newPath;
        });
    }

    handleNonCriteriaSubmit() {
        if (this.wizardSubmitSettled) {
            return;
        }
        if (
            this.embedFormLayout === 'wizard'
            && this.wizardMainComplete
            && (this.hasPendingWizardPostMainSteps() || !this.validateVisiblePostMainExtras(false))
        ) {
            return;
        }
        if (!this._wizardSubmitInFlight && !this.markWizardSubmitInFlight()) {
            return;
        }
        if (!this.validateVisiblePostMainExtras(true)) {
            this.clearWizardSubmitInFlight();
            alert(__('landing.embed_wizard_extra_required_alert'));
            return;
        }

        const selectedLang = $('.language-dropdown .language-option.active').data('lang')
                || $('input[name="language"]').val()
                || this.lang
                || 'en';
            const nonCriteriaText = $('#btn-register').data('noncriteria-text');

            const useWizardThankYou = typeof window.landingEmbedWizardThankYou === 'function'
                && window.landingEmbedWizardThankYou(this.embedFormLayout);
            const thankYouText = typeof window.landingEmbedResolveThankYouMessage === 'function'
                ? window.landingEmbedResolveThankYouMessage(null)
                : ($('#btn-register').attr('data-thank-you-text') || __('landing.embed_wizard_thank_you_fallback'));

            if (useWizardThankYou && typeof window.landingEmbedWizardSubmitStart === 'function') {
                window.landingEmbedWizardSubmitStart(this.embedFormLayout);
            }
            $('#btn-register').prop('disabled', true).addClass('is-loading');

            // Remove other flow keys before saving
            localStorage.removeItem('whatsappFinalData');
            localStorage.removeItem('calendarFinalData');
            localStorage.setItem('registerFinalData', JSON.stringify({
                text: nonCriteriaText,
                updatedAt: Date.now(),
                is_qualified: $(document).data('isQualified'),
            }));

            updateStatusAfterSubmit({
                _token: $('input[name="_token"]').val(),
                inscription_guid: $(document).data('inscriptionGuid'),
                is_qualified: $(document).data('isQualified')
            });

            let redirectUrl = `/${selectedLang}/landing/calendar/register/done`;
            const params = new URLSearchParams();
            if (this.landingSlug) {
                const inscriptionGuid = $(document).data('inscriptionGuid');

                if (inscriptionGuid) {
                    const tokenData = this.landingSlug + '|' + inscriptionGuid;
                    const token = btoa(tokenData);
                    params.set('token', token);
                } else {
                    params.set('slug', this.landingSlug);
                }
            }
            if (this.embedTheme) {
                params.set('embed_theme', this.embedTheme);
            }
            const redirectDisabledRaw = this.redirectEnabled === 'false' || this.redirectEnabled === false || String(this.redirectEnabled).toLowerCase() === 'false';
            const redirectDisabled = redirectDisabledRaw;
            if (redirectDisabled) {
                params.set('redirect_enabled', 'false');
            }
            const query = params.toString();
            if (query) {
                redirectUrl += (redirectUrl.indexOf('?') !== -1 ? '&' : '?') + query;
            }

            const usePostMessage = !!(window.parent && window.parent !== window);

            $.ajax({
                url: redirectUrl,
                method: 'GET',
                headers: {
                    'X-Form-Submitted': 'true', 
                    'X-Requested-With': 'XMLHttpRequest'
                }
            }).done(() => {
                // iframe
                if (usePostMessage && parent?.postMessage) {
                    let fullRedirectUrl = window.location.origin + (redirectUrl.indexOf('/') === 0 ? redirectUrl : '/' + redirectUrl);
                    const postMessagePayload = { type: 'form_submission_success', redirect_url: fullRedirectUrl };
                    try {
                        const stored = localStorage.getItem('registerFinalData');
                        if (stored) {
                            const finalData = JSON.parse(stored);
                            postMessagePayload.final_data = finalData;
                            const encoded = encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(finalData)))));
                            fullRedirectUrl += (fullRedirectUrl.indexOf('?') !== -1 ? '&' : '?') + 'embed_final=' + encoded;
                            postMessagePayload.redirect_url = fullRedirectUrl;
                        }
                    } catch (e) {
                        console.warn('[Embed calendar] Could not encode final_data for URL (load-questions):', e);
                    }
                    console.log('[Embed calendar] PostMessage payload (load-questions):', postMessagePayload);
                    parent.postMessage(postMessagePayload, '*');
                    const showThankYouAndReset = this.redirectFalseShowThankYouAndReset !== false && String(this.redirectFalseShowThankYouAndReset).toLowerCase() !== 'false';
                    if (showThankYouAndReset) {
                        $('#btn-submit-wrapper').find('.submit-success-msg').remove();
                        if (typeof useWizardThankYou !== 'undefined' && useWizardThankYou) {
                            if (typeof window.showLandingEmbedWizardThankYou === 'function') {
                                window.showLandingEmbedWizardThankYou(thankYouText);
                            } else {
                                $('#btn-register').prop('disabled', false).removeClass('is-loading');
                            }
                            $(document).trigger('embedCalendarRedirectDone');
                        } else {
                            const safeThankYou = typeof window.landingEmbedThankYouEscapeHtml === 'function'
                                ? window.landingEmbedThankYouEscapeHtml(thankYouText)
                                : thankYouText;
                            $('#btn-register').prop('disabled', false).removeClass('is-loading');
                            $('#btn-submit-wrapper').prepend('<p class="submit-success-msg has-text-success mt-2 mb-2">' + safeThankYou + '</p>');
                            $(document).trigger('embedCalendarRedirectDone');
                        }
                    } else {
                        if (typeof useWizardThankYou !== 'undefined' && useWizardThankYou && typeof window.showLandingEmbedWizardThankYou === 'function') {
                            window.showLandingEmbedWizardThankYou(thankYouText);
                        } else {
                            $('#btn-register').prop('disabled', false).removeClass('is-loading');
                        }
                        $('#form-calendar').find('input:not([type="hidden"]), select, textarea').prop('disabled', true);
                    }
                }
                // non-iframe fallback
                else {
                    window.location.href = redirectUrl;
                }
            }).fail(() => {
                this.clearWizardSubmitInFlight();
                $('#btn-register').prop('disabled', false).removeClass('is-loading');
                if (typeof useWizardThankYou !== 'undefined' && useWizardThankYou) {
                    if (typeof window.landingEmbedRestoreWizardInnerAfterSubmitError === 'function') {
                        window.landingEmbedRestoreWizardInnerAfterSubmitError();
                    }
                    return;
                }
                if (typeof window.landingEmbedRestoreWizardInnerAfterSubmitError === 'function') {
                    window.landingEmbedRestoreWizardInnerAfterSubmitError();
                }
            });
    }

    async getCountryName() {
        const data = await getGeoData();
        this.countryCode = data?.country_code?.toLowerCase() ?? null;
        this.countryName = data?.country_name ?? null;
    }

    ensureNumberPlaceholders() {
        $('.number-input').not('.landing-embed-number-wheel-input').each(function() {
            const $input = $(this);
            const pattern = $input.attr('pattern');
            const allowDecimal = Boolean(pattern && pattern.includes('\\.'));
            const minRaw = $input.attr('data-min');
            const maxRaw = $input.attr('data-max');
            const min = minRaw !== undefined && minRaw !== '' ? Number(minRaw) : undefined;
            const max = maxRaw !== undefined && maxRaw !== '' ? Number(maxRaw) : undefined;

            $input.off('.landingNumber');

            $input.on('input.landingNumber', function() {
                let value = $(this).val();

                if (allowDecimal) {
                    value = value.replace(/[^0-9.]/g, '');
                    const parts = value.split('.');
                    if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                    }
                } else {
                    value = value.replace(/[^0-9]/g, '');
                }

                $(this).val(value);
            });

            $input.on('blur.landingNumber', function() {
                const value = $(this).val();
                if (value === '' || value === null) {
                    return;
                }
                const numValue = parseFloat(value);
                if (Number.isNaN(numValue)) {
                    return;
                }
                if (min !== undefined && !Number.isNaN(min) && numValue < min) {
                    $(this).val(String(allowDecimal ? min : Math.round(min)));
                } else if (max !== undefined && !Number.isNaN(max) && numValue > max) {
                    $(this).val(String(allowDecimal ? max : Math.round(max)));
                }
            });

            $input.on('keydown.landingNumber', function(e) {
                if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
                    (e.keyCode === 65 && e.ctrlKey === true) ||
                    (e.keyCode >= 35 && e.keyCode <= 40) ||
                    (e.keyCode >= 48 && e.keyCode <= 57) ||
                    (e.keyCode >= 96 && e.keyCode <= 105)) {
                    if ((e.keyCode === 110 || e.keyCode === 190) && !allowDecimal) {
                        e.preventDefault();
                    }
                    return;
                }
                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                    e.preventDefault();
                }
            });
        });
    }

    ensurePlaceholders() {
        // Ensure placeholders are set correctly for number inputs
        const placeholderText = __('landing.input_placeholder');
        
        if (!placeholderText) {
            return;
        }
        
        // Set placeholder for number inputs
        $('.number-input').not('.landing-embed-number-wheel-input').attr('placeholder', placeholderText);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('keydown', function(e) {
        if (
            e.target.matches('input[type=number]') &&
            ['e', 'E', '+', '-'].includes(e.key)
        ) {
            e.preventDefault();
        }
    });
});

function landingEmbedThankYouEscapeHtml(s) {
    if (s === null || s === undefined) {
        return '';
    }
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

window.landingEmbedThankYouEscapeHtml = landingEmbedThankYouEscapeHtml;

window.landingEmbedWizardThankYou = function (embedFormLayout) {
    if (typeof window.showLandingEmbedWizardThankYou !== 'function') {
        return false;
    }
    if (embedFormLayout === 'wizard') {
        return true;
    }
    if ($('#questions-content .landing-wizard-inner').length > 0) {
        return true;
    }
    // Standard embed inside an iframe: stay inline with the themed thank-you.
    return !!($('#landing-embed-thank-you-source').length && window.parent && window.parent !== window);
};

// Move #calendar-wrapper back next to #questions-wrapper after deferred how_continue.
window.landingEmbedRestoreCalendarWrapperToDefaultSlot = function () {
    const $cal = $('#calendar-wrapper');
    const $qw = $('#questions-wrapper');
    if (!$cal.length || !$qw.length) {
        return;
    }
    if ($cal.prev().is($qw)) {
        return;
    }
    $cal.detach().insertAfter($qw);
};

window.landingEmbedResolveThankYouMessage = function (thankYouFromApi, howContinueOverride) {
    const $how = $('#how_continue');
    const rawHow = howContinueOverride !== undefined && howContinueOverride !== null
        ? howContinueOverride
        : ($how.length ? $how.val() : '');
    const howVal = rawHow !== null && rawHow !== undefined ? String(rawHow).trim() : '';
    const $btn = $('#btn-register');
    if (howVal && $btn.length) {
        let per = $btn.attr('data-thank-you-' + howVal);
        if (!per) {
            per = $btn.attr('data-thank-you-' + howVal.toLowerCase());
        }
        if (per) {
            return per;
        }
    }
    if (thankYouFromApi) {
        return thankYouFromApi;
    }
    if (!$btn.length) {
        return __('landing.embed_wizard_thank_you_fallback');
    }
    return $btn.attr('data-thank-you-text') || __('landing.embed_wizard_thank_you_fallback');
};

window.landingEmbedLockWizardInnerStepsHidden = function () {
    const loader = window.__landingQuestionsLoaderInstance;
    if (loader && typeof loader.lockWizardInnerStepsHidden === 'function') {
        loader.lockWizardInnerStepsHidden();
        return;
    }
    $('.landing-wizard-inner').children().addClass('hidden');
};

window.landingEmbedRestoreWizardInnerAfterSubmitError = function () {
    const loader = window.__landingQuestionsLoaderInstance;
    $('#questions-loader').addClass('hidden');

    if (loader && typeof loader.clearWizardSubmitInFlight === 'function') {
        loader.clearWizardSubmitInFlight();
    }

    if (loader && loader.wizardSubmitSettled) {
        return;
    }

    if (loader && loader.wizardMainComplete) {
        $('.landing-wizard-nav, .landing-wizard-progress').removeClass('hidden');
        $('#btn-submit-wrapper').addClass('hidden');
        if (typeof loader.syncEmbedWizardProgressForPostInnerExtras === 'function') {
            loader.syncEmbedWizardProgressForPostInnerExtras();
        }
        if (typeof loader.syncWizardNav === 'function') {
            loader.syncWizardNav();
        }
        if (loader.$wizardNext) {
            loader.$wizardNext.removeClass('is-loading');
        }
        return;
    }

    const $inner = $('.landing-wizard-inner');
    if (!$inner.length) {
        return;
    }
    $inner.removeClass('hidden');
    if (loader && typeof loader.updateWizardStepVisibility === 'function') {
        loader.updateWizardStepVisibility();
        loader.wizardNormalizeCurrentStepVisualState();
    } else {
        $inner.children().addClass('hidden');
        $inner.children().first().removeClass('hidden');
    }
};

// Show the loader and hide form chrome during submit/redirect.
window.landingEmbedWizardSubmitStart = function (embedFormLayout) {
    if (typeof window.landingEmbedWizardThankYou !== 'function' || !window.landingEmbedWizardThankYou(embedFormLayout)) {
        return;
    }
    if (typeof window.landingEmbedLockWizardInnerStepsHidden === 'function') {
        window.landingEmbedLockWizardInnerStepsHidden();
    }
    $('.landing-wizard-nav, .landing-wizard-progress').addClass('hidden');
    $('#btn-submit-wrapper').addClass('hidden');
    $('#questions-content > .extra-question').addClass('hidden');
    $('#calendar-wrapper').addClass('hidden');
    const $inner = $('.landing-wizard-inner');
    if ($inner.length) {
        $inner.addClass('hidden');
    }
    $('#questions-content').removeClass('hidden');
    $('#questions-loader').removeClass('hidden');
};

window.showLandingEmbedWizardThankYou = function (plainMessage) {
    if (window.__landingEmbedWizardSubmitSettled) {
        return;
    }
    const loader = window.__landingQuestionsLoaderInstance;
    if (loader?.wizardSubmitSettled) {
        return;
    }
    if (loader && typeof loader.clearWizardSubmitInFlight === 'function') {
        loader.clearWizardSubmitInFlight();
    }
    window.__landingEmbedWizardSubmitSettled = true;
    if (loader) {
        loader.wizardSubmitSettled = true;
    }
    if (typeof window.landingEmbedLockWizardInnerStepsHidden === 'function') {
        window.landingEmbedLockWizardInnerStepsHidden();
    }
    if (typeof window.landingEmbedRestoreCalendarWrapperToDefaultSlot === 'function') {
        window.landingEmbedRestoreCalendarWrapperToDefaultSlot();
    }
    $('.landing-wizard-nav, .landing-wizard-progress').addClass('hidden');
    $('#questions-content > .extra-question').addClass('hidden');
    $('#btn-submit-wrapper').addClass('hidden');
    $('#questions-loader').addClass('hidden');
    $('#calendar-wrapper').addClass('hidden');
    $('#questions-content').removeClass('hidden');
    $('#questions-wrapper').removeClass('landing-wizard-how-continue-pending');

    const config = window.landingEmbedThankYouConfig || { embedTheme: 'default' };
    if (loader?.embedTheme) {
        config.embedTheme = loader.embedTheme;
    }
    const data = getLatestCalendarFinalData();
    if (data && plainMessage && !data.text) {
        data.text = plainMessage;
    }
    const detailsHtml = buildCalendarFinalDetailsHtml(data, config);

    const $source = $('#landing-embed-thank-you-source');
    let $thankYouRoot = null;

    if ($source.length && $source.children().length) {
        // Match the non-embed thank-you's wrapper classes so theme CSS applies.
        $thankYouRoot = $('<div class="landing-embed-thank-you-root form-wrapper success-message"></div>');
        $source.children().clone().appendTo($thankYouRoot);
        const $details = $thankYouRoot.find('#calendar-final-details').first();
        if ($details.length) {
            $details.html(detailsHtml);
        } else if (detailsHtml) {
            $thankYouRoot.append(`<div class="landing-embed-thank-you-details">${detailsHtml}</div>`);
        }
    } else {
        const messageHtml = landingEmbedThankYouEscapeHtml(plainMessage || '');
        $thankYouRoot = $(
            '<div class="landing-wizard-thank-you form-wrapper success-message content p-4 has-text-centered">' +
                '<p class="submit-success-msg has-text-success is-size-5">' + messageHtml + '</p>' +
            '</div>'
        );
    }

    const $inner = $('.landing-wizard-inner');
    if ($inner.length) {
        $inner.empty().append($thankYouRoot).removeClass('hidden');
    } else {
        $('#questions-content').empty().append($thankYouRoot);
    }

    if (window.landingEmbedThemeConfig?.scrollToTopOnSubmit) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }

    $('#btn-register').prop('disabled', false).removeClass('is-loading');
};


LandingQuestionsLoader._calendarSubmitLifecycleBound = false;

// Make it available globally
window.LandingQuestionsLoader = LandingQuestionsLoader;
